<template>
  <div class="Kawalanpenerbitan">
    <v-snackbar :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
      {{ snackbar.message }}
    </v-snackbar>

    <PageHeader
      icon="mdi-file-table-box-multiple"
      title="Pamer Data HIES"
      subtitle="Eksport data untuk penerbitan, muat naik semula, dan kawalan aliran semak &amp; sah"
    />

    <div>
      <v-card outlined>
        <!-- Kepala: tajuk di kiri, akses pengguna di kanan. Cip kebenaran
             dikumpul di bawah label "AKSES ANDA" dan bukan dibiarkan terapung
             bersebelahan tajuk — tanpa label, cip seperti "UPLOAD"/"SEMAK"
             kelihatan seperti penapis atau status batch, bukan kebenaran
             pengguna. `flex-wrap` + `gap` supaya baris ini membalut dengan
             kemas pada skrin sempit dan bukan menolak butang keluar. -->
        <v-card-title class="py-3 kad__tajuk kp-head">
          <div class="d-flex align-center">
            <v-icon small class="mr-2">mdi-file-table-box-multiple</v-icon>
            <span class="subtitle-1 font-weight-medium">Pamer Data HIES</span>
          </div>
          <v-spacer></v-spacer>
          <div v-if="roleAllowed" class="d-flex align-center kp-akses">
            <!-- <span class="kp-akses__label">Akses anda</span> -->
            <v-chip v-if="myPerms.isAdmin" small class="kp-chip kp-chip--admin" label>
              <v-icon small left>mdi-shield-crown</v-icon>ADMIN — akses penuh
            </v-chip>
            <template v-for="st in aksesStages">
              <v-chip
                :key="st.key"
                small label
                class="kp-chip"
                :class="{ 'kp-chip--off': !st.granted }"
                :title="st.granted ? 'Anda ada kebenaran ' + st.label : 'Tiada kebenaran ' + st.label"
              >
                <v-icon small left>{{ st.granted ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}</v-icon>{{ st.label }}
              </v-chip>
              <!-- Work waiting on this user at that stage. Grey at zero, red
                   once there is something to do; hidden entirely without the
                   permission, since the count would not be actionable. -->
              <v-chip
                v-if="st.granted"
                :key="st.key + '-count'"
                x-small
                class="kp-count"
                :class="{ 'kp-count--on': st.count > 0 }"
                :title="st.count > 0
                  ? `${st.count} penerbitan menunggu tindakan ${st.label} — klik untuk tapis senarai`
                  : `Tiada penerbitan menunggu tindakan ${st.label}`"
                @click="jumpToBatches(st.filter)"
              >{{ st.count }}</v-chip>
            </template>
            <!-- Cancellations the user has not acknowledged. Clicking only
                 FILTERS the list — the count drops one at a time as each is
                 actually opened, so it tracks what has really been read. -->
            <v-chip
              v-if="batalCount"
              small label dark
              class="kp-chip kp-chip--batal"
              title="Penerbitan anda yang dibatalkan — klik untuk tapis senarai"
              @click="jumpToBatches('Batal')"
            >Batal {{ batalCount }}</v-chip>
            <v-btn v-if="myPerms.isAdmin" class="btn-teal text-none ml-2" @click="openPermManager">
              <v-icon left>mdi-account-key</v-icon> Urus Kebenaran
            </v-btn>
          </div>
        </v-card-title>

        <!-- Still resolving the user's access. Disallowed roles are redirected
             to the separate no-access page (see init) and never render below. -->
        <v-container fluid v-if="!permsLoaded || !roleAllowed">
          <v-skeleton-loader type="list-item-two-line"></v-skeleton-loader>
        </v-container>

        <!-- No access notice -->
        <v-container fluid v-else-if="!hasAnyAccess">
          <v-alert type="warning" prominent border="left">
            Anda tiada kebenaran (Upload / Semak / Sah) untuk modul Pamer Data HIES.
            Sila hubungi pentadbir sistem.
          </v-alert>
        </v-container>

        <v-container fluid v-else>
          <!-- ============ EXPORT + LIVE PREVIEW ============ -->
          <v-row>
            <v-col cols="12">
              <v-card outlined class="pa-4">
                <div class="d-flex align-center mb-3 flex-wrap" style="gap:8px">
                  <v-icon color="#037769" class="mr-2">mdi-database-export-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold">Eksport CSV (untuk penerbitan)</span>
                  <v-spacer></v-spacer>
                  <v-chip small label color="#037769" outlined v-if="preview" class="font-weight-medium">
                    <v-icon left x-small>mdi-table-large</v-icon>
                    {{ preview.total.toLocaleString() }} baris · {{ preview.columns.length }} lajur
                  </v-chip>
                </div>

                <!-- Bar penapis eksport — susunan yang SAMA seperti Kadar Respon
                     dan Jadual Awalan: jalur teal, label kening di ATAS medan.
                     Label terapung dibuang kerana ia beralih tempat apabila
                     medan diisi; `persistent-placeholder` menggantikannya supaya
                     medan kosong masih menunjukkan apa yang boleh dipilih. -->
                <div class="filter-bar">
                  <v-row dense align="end">
                    <v-col cols="12" sm="6" md="3">
                      <div class="fld__label">Negeri</div>
                      <v-autocomplete outlined dense hide-details :items="lookupnegeri"
                        placeholder="Pilih negeri" persistent-placeholder
                        item-text="negeri" item-value="negeriID" v-model="expNegeri"
                        @change="onNegeriChange"></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="fld__label">Daerah Pentadbiran</div>
                      <v-autocomplete outlined dense hide-details clearable
                        placeholder="Semua Daerah Pentadbiran" persistent-placeholder
                        :items="lookupdaerah" item-text="NamaDP" item-value="DP" v-model="expDaerah"
                        :disabled="!expNegeri"
                        :no-data-text="expNegeri ? 'Tiada daerah' : 'Pilih negeri dahulu'"
                        @change="fetchPreview">
                        <template v-slot:item="{ item }">
                          <span class="mr-2 grey--text">{{ item.DP }}</span> {{ item.NamaDP }}
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="fld__label">Jadual</div>
                      <v-autocomplete outlined dense hide-details return-object
                        placeholder="Pilih jadual" persistent-placeholder
                        :items="jadualList" item-text="label" v-model="expJadual"
                        @change="fetchPreview"></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="fld__label">Pusingan</div>
                      <v-autocomplete outlined dense hide-details return-object
                        placeholder="Pilih pusingan" persistent-placeholder
                        :items="lookuppusingan" :item-text="i => i.pusingan" v-model="expPusingan"
                        @change="fetchPreview"></v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <v-alert v-if="expJadual && !expJadual.exportable" type="info" dense text class="mt-3 mb-0">
                  Jadual ini tidak menyokong format Pamer Data HIES: {{ expJadual.reason }}
                  <br />Sila guna eksport biasa di Pamer Data Hies dan maklumkan pentadbir.
                </v-alert>

                <div class="d-flex align-center mt-4 pa-3 rounded flex-wrap" style="gap:16px; background-color: #f4f6f8; border: 1px solid #e2e8f0;">
                  <v-btn class="btn-teal text-none" height="40" :loading="exporting" :disabled="!canExport" @click="downloadCsv">
                    <v-icon left>mdi-download</v-icon> Muat Turun CSV
                  </v-btn>

                  <v-divider vertical style="height: 24px; align-self: center;"></v-divider>

                  <v-btn class="btn-green text-none" height="40" :loading="uploading" :disabled="!canUpload" @click="triggerUpload">
                    <v-icon left>mdi-upload</v-icon> Muat Naik CSV
                  </v-btn>
                  <input ref="uploadInput" type="file" accept=".csv" hidden @change="onFilePicked">

                  <v-spacer></v-spacer>
                  
                  <div class="d-flex align-center" v-if="preview">
                    <div class="text-right mr-3">
                      <div class="caption grey--text text--darken-2 mb-n1">
                        Pratonton {{ preview.shown }} / {{ preview.total.toLocaleString() }} baris
                      </div>
                      <div class="caption grey--text text--darken-2">
                        Kekunci Utama: <b class="primary--text">{{ preview.pk }}</b>
                      </div>
                    </div>
                    <v-btn v-if="canExport" icon :loading="previewLoading"
                      title="Muat semula pratonton" @click="fetchPreview">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Live preview snippet -->
                <div class="mt-3">
                  <v-skeleton-loader v-if="previewLoading" type="table-heading, table-row-divider@4"></v-skeleton-loader>

                  <template v-else-if="preview">
                    <div class="d-flex align-center flex-wrap mb-1" style="gap:6px">
                      <v-chip x-small label :color="previewFilterActive ? 'orange lighten-4' : 'grey lighten-3'">
                        {{ preview.total.toLocaleString() }} baris
                        <span v-if="previewFilterActive" class="ml-1">sepadan</span>
                      </v-chip>
                      <v-btn x-small text v-if="previewFilterActive" @click="clearPreviewFilters">
                        <v-icon left x-small>mdi-filter-off</v-icon>Kosongkan tapisan lajur
                      </v-btn>
                    </div>
                    <div class="preview-wrap">
                      <table class="preview-table">
                        <thead>
                          <tr>
                            <th class="idx-col">#</th>
                            <!-- Label comes from the pamer_data config; the key
                                 stays the real column name because that is how
                                 the rows are keyed and how the CSV is written. -->
                            <th v-for="c in preview.columns" :key="c" :class="{ 'pk-col': c === preview.pk }"
                              :title="colLabel(c) === c ? c : `${colLabel(c)}  (${c})`">
                              {{ colLabel(c) }}
                              <v-icon v-if="c === preview.pk" x-small color="#037769">mdi-key</v-icon>
                            </th>
                          </tr>
                          <!-- One filter box per column. Narrows the rows already
                               loaded below; it does not re-query the server. -->
                          <tr class="filter-row">
                            <th class="idx-col">
                              <v-icon x-small color="grey">mdi-filter-variant</v-icon>
                            </th>
                            <th v-for="c in preview.columns" :key="'f' + c">
                              <input class="col-filter" type="text" :placeholder="colLabel(c)"
                                :value="previewFilters[c] || ''"
                                @input="setPreviewFilter(c, $event.target.value)">
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(r, i) in preview.rows" :key="i">
                            <td class="idx-col">{{ previewRowOffset + i + 1 }}</td>
                            <td v-for="c in preview.columns" :key="c"
                              :class="{ 'pk-col': c === preview.pk, 'empty-cell': !r[c] }" :title="r[c]">
                              {{ r[c] }}
                            </td>
                          </tr>
                          <tr v-if="!preview.rows.length">
                            <td :colspan="preview.columns.length + 1" class="text-center grey--text py-4">
                              Tiada baris sepadan dengan tapisan lajur.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <!-- Footer laid out like the Senarai Penerbitan data table:
                         rows-per-page then the range then the pager, bottom right. -->
                    <div class="preview-footer">
                      <span class="pf-label">Baris setiap halaman:</span>
                      <v-select :value="previewPerPage" @change="setPreviewPerPage"
                        :items="[25, 50, 100, 200, 500]" dense hide-details
                        class="pf-select"></v-select>
                      <span class="pf-range">
                        {{ (previewRowOffset + 1).toLocaleString() }}&ndash;{{ (previewRowOffset + preview.shown).toLocaleString() }}
                        daripada {{ preview.total.toLocaleString() }}
                      </span>
                      <v-btn icon small :disabled="previewPage <= 1" @click="setPreviewPage(previewPage - 1)">
                        <v-icon>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn icon small :disabled="previewPage >= preview.pageCount" @click="setPreviewPage(previewPage + 1)">
                        <v-icon>mdi-chevron-right</v-icon>
                      </v-btn>
                    </div>
                    <div class="caption grey--text mt-1">
                      <span v-if="previewFilterActive">
                        Tapisan lajur hanya mempengaruhi pratonton — fail CSV yang dieksport sentiasa mengandungi
                        semua baris bagi Negeri/Jadual/Pusingan yang dipilih.
                      </span>
                      Dua lajur tambahan (<b>{{ preview.pk }}</b> &amp; <b>tanda_untuk_padam</b>) ditambah di hujung fail CSV.
                    </div>
                  </template>

                  <!-- The empty case is now a row inside the table, so the
                       filter boxes stay on screen and can be corrected. -->
                  <div v-else class="text-center grey--text py-8 preview-empty">
                    <v-icon size="48" color="grey lighten-1">mdi-table-search</v-icon>
                    <div class="mt-2">Pilih Negeri, Jadual &amp; Pusingan untuk pratonton data.</div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- ============ BATCH LISTING ============ -->
          <v-row class="mt-2">
            <v-col cols="12" ref="batchList">
              <div class="d-flex align-center mb-2">
                <div class="text-subtitle-1 font-weight-bold">
                  <v-icon left>mdi-format-list-checks</v-icon> Senarai Penerbitan
                </div>
                <v-spacer></v-spacer>
                <span class="caption grey--text mr-3" v-if="anyFilterActive">
                  {{ filteredBatches.length }} / {{ batches.length }} batch
                </span>
                <v-btn small text v-if="anyFilterActive" @click="clearAllFilters">
                  <v-icon left small>mdi-filter-off</v-icon>Kosongkan Tapisan
                </v-btn>
              </div>
              <v-data-table :headers="batchHeaders" :items="filteredBatches" :items-per-page="10"
                :loading="loadingBatches" class="elevation-1 batch-table"
                @click:row="item => openBatch(item.id)">

                <!-- ===== in-header filters ===== -->
                <template v-slot:header.id="{ header }">
                  <div class="th-filter th-filter--center">
                    <span class="th-label">{{ header.text }}</span>
                  </div>
                </template>
                <template v-slot:header.jadual="{ header }">
                  <div class="th-filter">
                    <span class="th-label">{{ header.text }}</span>
                    <v-select :items="jadualOptions" v-model="filters.jadual" dense solo flat hide-details
                      clearable placeholder="Semua" class="th-input" @click.stop></v-select>
                  </div>
                </template>
                <template v-slot:header.ng="{ header }">
                  <div class="th-filter">
                    <span class="th-label">{{ header.text }}</span>
                    <v-select :items="ngOptions" v-model="filters.ng" dense solo flat hide-details
                      clearable placeholder="Semua" class="th-input" @click.stop></v-select>
                  </div>
                </template>
                <template v-slot:header.pusingan="{ header }">
                  <div class="th-filter">
                    <span class="th-label">{{ header.text }}</span>
                    <v-select :items="pusinganOptions" v-model="filters.pusingan" dense solo flat hide-details
                      clearable placeholder="Semua" class="th-input" @click.stop></v-select>
                  </div>
                </template>
                <template v-slot:header.filename="{ header }">
                  <div class="th-filter th-filter--center">
                    <span class="th-label">{{ header.text }}</span>
                  </div>
                </template>
                <template v-slot:header.counts="{ header }">
                  <div class="th-filter th-filter--center">
                    <span class="th-label">{{ header.text }}</span>
                  </div>
                </template>
                <template v-slot:header.status="{ header }">
                  <div class="th-filter">
                    <span class="th-label">{{ header.text }}</span>
                    <v-select :items="statusOptions" v-model="filters.status" dense solo flat hide-details
                      clearable placeholder="Semua" class="th-input" @click.stop></v-select>
                  </div>
                </template>
                <template v-slot:header.uploaded_by_name="{ header }">
                  <div class="th-filter">
                    <span class="th-label">{{ header.text }}</span>
                    <v-text-field v-model="olehSearch" dense solo flat hide-details clearable
                      placeholder="Cari…" prepend-inner-icon="mdi-magnify" class="th-input"
                      @click.stop @input="onOlehInput" @click:clear="onOlehClear"></v-text-field>
                  </div>
                </template>
                <template v-slot:header.created_at="{ header }">
                  <div class="th-filter">
                    <span class="th-label">
                      {{ header.text }}
                      <!-- Reset every filter and reload in one action. Replaces
                           the old Muat Semula button, which only reloaded. -->
                      <v-btn icon x-small class="ml-1" :loading="loadingBatches"
                        title="Set semula tapisan &amp; muat semula senarai"
                        @click.stop="resetAndReload">
                        <v-icon x-small>mdi-refresh</v-icon>
                      </v-btn>
                    </span>
                    <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small outlined class="text-none th-date" v-bind="attrs" v-on="on" @click.stop>
                          <v-icon left x-small>mdi-calendar</v-icon>{{ dateLabel }}
                        </v-btn>
                      </template>
                      <v-date-picker v-model="dateRange" range no-title scrollable @change="onDateRange">
                        <v-btn text small @click="clearDate">Kosong</v-btn>
                        <v-spacer></v-spacer>
                        <v-btn text small color="#037769" @click="dateMenu = false">OK</v-btn>
                      </v-date-picker>
                    </v-menu>
                  </div>
                </template>

                <!-- ===== cells ===== -->
                <template v-slot:item.status="{ item }">
                  <v-chip small :color="statusColor(item.status)" dark>{{ stageLabel(item.status) }}</v-chip>
                </template>
                <template v-slot:item.counts="{ item }">
                  <span class="green--text">+{{ item.new_count }}</span> /
                  <span class="orange--text">~{{ item.update_count }}</span> /
                  <span class="red--text">-{{ item.delete_count }}</span>
                </template>
                <template v-slot:item.created_at="{ item }">{{ fmtDate(item.created_at) }}</template>
                <template v-slot:item.action="{ item }">
                  <v-btn small class="btn-teal text-none" :class="{ 'pulse-action-btn': requiresAction(item) }" @click.stop="openBatch(item.id)">
                    <v-icon left small>mdi-eye</v-icon>Lihat
                  </v-btn>
                </template>

                <template v-slot:no-data>
                  <div class="grey--text py-6">
                    {{ anyFilterActive ? 'Tiada batch sepadan dengan tapisan.' : 'Tiada batch lagi.' }}
                  </div>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </div>

    <!-- ============ UPLOAD PROCESSING (blocking) ============ -->
    <!-- persistent + no close button: the user cannot dismiss it or interact
         with the page while the CSV is being compared against the live table. -->
    <v-dialog v-model="uploadDialog" persistent no-click-animation max-width="460">
      <v-card class="pa-6 text-center">
        <!-- 1a. UPLOADING — the bytes are still travelling to the server. On a
             slow line this is where most of the wait is, so it gets a real
             percentage instead of the same spinner as the comparison step. -->
        <template v-if="uploading && uploadPhase === 'upload'">
          <v-progress-circular :indeterminate="!uploadTotal" :value="uploadPct"
            size="56" width="5" color="#037769">
            <span v-if="uploadTotal" class="caption font-weight-bold">{{ uploadPct }}%</span>
          </v-progress-circular>
          <div class="text-subtitle-1 font-weight-bold mt-4">Langkah 1/2 · Memuat naik fail…</div>
          <div class="caption grey--text text-truncate mt-1" v-if="uploadFileName" :title="uploadFileName">
            {{ uploadFileName }}
          </div>
          <v-progress-linear :indeterminate="!uploadTotal" :value="uploadPct"
            color="#037769" rounded height="8" class="mt-4"></v-progress-linear>
          <div class="caption grey--text mt-2">
            <span v-if="uploadTotal">{{ fmtBytes(uploadLoaded) }} / {{ fmtBytes(uploadTotal) }}</span>
            <span v-else>{{ fmtBytes(uploadLoaded) }} dihantar</span>
            <span v-if="uploadRate"> · {{ fmtBytes(uploadRate) }}/s</span>
            <span v-if="uploadEta !== null"> · anggaran {{ fmtEta(uploadEta) }} lagi</span>
          </div>
          <v-alert type="warning" dense text class="mt-4 mb-0 text-left">
            Jangan tutup atau tinggalkan halaman ini sehingga selesai.
          </v-alert>
        </template>

        <!-- 1b. PROCESSING — server has the whole file. Its internal steps are
             weighted into one 0-100 figure, so this is a single bar that only
             moves forward. -->
        <template v-else-if="uploading">
          <v-progress-circular :value="procPct" size="56" width="5" color="#037769">
            <span class="caption font-weight-bold">{{ procPct }}%</span>
          </v-progress-circular>
          <div class="text-subtitle-1 font-weight-bold mt-4">Langkah 2/2 · Memproses fail…</div>
          <div class="caption grey--text text-truncate mt-1" v-if="uploadFileName" :title="uploadFileName">
            {{ uploadFileName }}
          </div>
          <div class="caption success--text mt-1">
            <v-icon x-small color="success">mdi-check-circle</v-icon>
            Muat naik selesai<span v-if="uploadTotal"> ({{ fmtBytes(uploadTotal) }})</span>
          </div>
          <v-progress-linear :value="procPct" color="#037769" rounded height="8" class="mt-4"></v-progress-linear>
          <!-- The bar above is one continuous 0-100. This line is the live
               count, so a long step still visibly ticks over. -->
          <div class="caption grey--text mt-2" style="min-height:18px">
            <span v-if="procLabel">{{ procLabel }}</span>
            <span v-if="procTotal">
              · {{ procDone.toLocaleString() }} / {{ procTotal.toLocaleString() }} {{ procUnit }}
            </span>
          </div>
          <v-alert type="warning" dense text class="mt-3 mb-0 text-left">
            Jangan tutup atau tinggalkan halaman ini sehingga selesai.
          </v-alert>
        </template>

        <!-- 2. FAILED -->
        <template v-else-if="uploadError">
          <v-icon size="56" color="error">mdi-alert-circle-outline</v-icon>
          <div class="text-subtitle-1 font-weight-bold mt-3">Muat naik gagal</div>
          <div class="caption grey--text text-truncate mt-1" v-if="uploadFileName" :title="uploadFileName">
            {{ uploadFileName }}
          </div>
          <v-alert type="error" dense text class="mt-4 mb-0 text-left">{{ uploadError }}</v-alert>
          <v-btn class="btn-teal text-none mt-5" @click="closeUploadDialog">Tutup</v-btn>
        </template>

        <!-- 3. DONE, but nothing to publish -->
        <template v-else>
          <v-icon size="56" color="info">mdi-file-check-outline</v-icon>
          <div class="text-subtitle-1 font-weight-bold mt-3">
            {{ uploadMessage || 'Tiada perbezaan antara fail dan data semasa.' }}
          </div>
          <div class="caption grey--text text-truncate mt-1" v-if="uploadFileName" :title="uploadFileName">
            {{ uploadFileName }}
          </div>
          <div class="mt-4" v-if="uploadResult">
            <v-chip small label class="ma-1">
              <v-icon left x-small>mdi-table-row</v-icon>
              {{ (uploadResult.totalRows || 0).toLocaleString() }} baris disemak
            </v-chip>
            <v-chip small label class="ma-1" color="grey lighten-3">
              {{ (uploadResult.ignored || 0).toLocaleString() }} tiada perubahan
            </v-chip>
          </div>
          <v-btn class="btn-teal text-none mt-5" @click="closeUploadDialog">Tutup</v-btn>
        </template>
      </v-card>
    </v-dialog>

    <!-- ============ BATCH DETAIL DIALOG ============ -->
    <v-dialog v-model="detailDialog" max-width="1100" scrollable>
      <v-card v-if="detail" class="kp-detail">
        <v-card-title class="grey lighten-2">
          Terbitan #{{ detail.batch.id }} — {{ detail.batch.table_name }}
          <v-chip small class="ml-3" :color="statusColor(detail.batch.status)" dark>{{ stageLabel(detail.batch.status) }}</v-chip>
          <!-- Drafts are hidden from everyone but their uploader, so say it here
               rather than leaving them wondering why nobody else sees it. -->
          <span v-if="!detail.batch.status" class="caption grey--text ml-2">
            Hanya pemuatnaik boleh lihat
          </span>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text style="max-height:75vh">
          <v-row class="mt-1">
            <v-col cols="12" md="6">
              <div class="caption">
                Jadual: <b>{{ detail.batch.jadual }}</b> ·
                Negeri: <b>{{ detail.batch.ng || '-' }}</b> ·
                Pusingan: <b>{{ detail.batch.pusingan || '-' }}</b> ·
                Fail: <b>{{ detail.batch.filename }}</b>
              </div>
              <div class="caption">
                Kekunci Utama: <b>{{ detail.batch.pk_column }}</b>
              </div>
              <div class="caption">
                Dimuat naik oleh <b>{{ detail.batch.uploaded_by_name }}</b> pada {{ fmtDate(detail.batch.created_at) }}
              </div>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center justify-end flex-wrap" style="gap:6px">
              <!-- Stage actions -->
              <!-- DRAFT (status NULL): uploader previews then decides -->
              <template v-if="!detail.batch.status">
                <v-btn small color="success" :disabled="!canUpload" @click="openAction('submit','Hantar')">
                  <v-icon left small>mdi-send-check-outline</v-icon>Hantar untuk Semakan
                </v-btn>
                <v-btn small color="error" :disabled="!canUpload" @click="openAction('bataldraft','Batal')">
                  <v-icon left small>mdi-delete-outline</v-icon>Batal
                </v-btn>
                <div v-if="!canUpload" class="caption grey--text w-100 text-right">Perlu kebenaran Upload</div>
              </template>
              <template v-else-if="detail.batch.status === 'Semak'">
                <v-btn small color="success" :disabled="!canSemak" @click="openAction('semak','Semak')">
                  <v-icon left small>mdi-check</v-icon>Semak
                </v-btn>
                <v-btn small color="error" :disabled="!canSemak" @click="openAction('semak','Batal')">
                  <v-icon left small>mdi-close</v-icon>Batal
                </v-btn>
                <div v-if="!canSemak" class="caption grey--text w-100 text-right">Perlu kebenaran Semak</div>
              </template>
              <template v-else-if="detail.batch.status === 'Sah'">
                <v-btn small color="success" :disabled="!canSah" @click="openAction('sah','Sah')">
                  <v-icon left small>mdi-check-decagram</v-icon>Sah
                </v-btn>
                <v-btn small color="error" :disabled="!canSah" @click="openAction('sah','Batal')">
                  <v-icon left small>mdi-close</v-icon>Batal
                </v-btn>
                <div v-if="!canSah" class="caption grey--text w-100 text-right">Perlu kebenaran Sah</div>
              </template>
              <template v-else>
                <v-chip small :color="statusColor(detail.batch.status)" dark>{{ detail.batch.status }}</v-chip>
              </template>
            </v-col>
          </v-row>

          <!-- Upload result summary — same numbers the upload popup reports,
               including the rows that matched and were ignored. -->
          <div class="d-flex align-center flex-wrap mt-2" style="gap:6px">
            <span class="caption grey--text">Hasil muat naik:</span>
            <v-chip x-small label color="green lighten-4">{{ detail.batch.new_count }} baru</v-chip>
            <v-chip x-small label color="orange lighten-4">{{ detail.batch.update_count }} kemas kini</v-chip>
            <v-chip x-small label color="red lighten-4">{{ detail.batch.delete_count }} padam</v-chip>
            <v-chip x-small label color="grey lighten-3">
              {{ Math.max(0, (detail.batch.total_rows || 0) - (detail.batch.new_count + detail.batch.update_count + detail.batch.delete_count)) }}
              tiada perubahan
            </v-chip>
            <span class="caption grey--text">daripada {{ (detail.batch.total_rows || 0).toLocaleString() }} baris fail</span>
            <v-spacer></v-spacer>
            <!-- Only the touched rows, in the re-uploadable export layout. This
                 is the fix-and-retry path after a Sah failure: a handful of rows
                 to correct instead of the whole hundred-thousand-row export. -->
            <v-btn small outlined class="text-none" :loading="batchCsvLoading"
              :disabled="!affectedRowCount" @click="downloadBatchCsv()">
              <v-icon left small>mdi-download</v-icon>
              Muat Turun CSV Terbitan ({{ affectedRowCount.toLocaleString() }} baris)
            </v-btn>
          </div>

          <!-- Tabs carry an icon and a count pill, sit on a tinted rail with a
               thick active underline. Plain text labels on a plain background
               did not read as controls at all. -->
          <v-tabs v-model="detailTab" class="mt-3 kp-tabs" show-arrows
            color="#037769" slider-color="#037769" slider-size="3">
            <v-tab class="text-none">
              <v-icon small left>mdi-plus-circle-outline</v-icon>Baru
              <span class="kp-tab-count kp-tab-count--new">{{ detail.batch.new_count }}</span>
            </v-tab>
            <v-tab class="text-none">
              <v-icon small left>mdi-pencil-outline</v-icon>Kemas Kini
              <span class="kp-tab-count kp-tab-count--upd">{{ detail.batch.update_count }}</span>
            </v-tab>
            <v-tab class="text-none">
              <v-icon small left>mdi-delete-outline</v-icon>Padam
              <span class="kp-tab-count kp-tab-count--del">{{ detail.batch.delete_count }}</span>
            </v-tab>
            <v-tab class="text-none">
              <v-icon small left>mdi-ruler</v-icon>Semakan Julat
              <template v-if="sjReport && sjReport.applicable">
                <v-icon v-if="sjReport.clean" small right color="success">mdi-check-circle</v-icon>
                <span v-else class="kp-tab-count kp-tab-count--err">{{ sjReport.summary.rowsWithIssues }}</span>
              </template>
              <v-progress-circular v-else-if="sjLoading" indeterminate size="12" width="2"
                class="ml-2" color="#037769"></v-progress-circular>
            </v-tab>
            <v-tab class="text-none">
              <v-icon small left>mdi-history</v-icon>Sejarah
              <span class="kp-tab-count" v-if="detail.history">{{ detail.history.length }}</span>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="detailTab">
            <!-- NEW -->
            <v-tab-item>
              <v-simple-table dense class="mt-2">
                <thead><tr><th>#</th><th v-for="c in keyCols" :key="c">{{ c }}</th><th>Lihat Semua</th></tr></thead>
                <tbody>
                  <tr v-for="(r,i) in detail.rows.new" :key="'n'+r.id" style="background:#e8f5e9">
                    <td>{{ i+1 }}</td>
                    <td v-for="c in keyCols" :key="c">{{ (r.new_data||{})[c] }}</td>
                    <td><v-btn x-small text @click="openRecord('new', r)">Lihat</v-btn></td>
                  </tr>
                  <tr v-if="!detail.rows.new.length"><td :colspan="keyCols.length+2" class="text-center grey--text">Tiada rekod baru</td></tr>
                </tbody>
              </v-simple-table>
              <div v-if="detail.batch.new_count > detail.rows.new.length" class="caption grey--text mt-1">
                Menunjukkan {{ detail.rows.new.length }} daripada {{ detail.batch.new_count }} (pratonton).
              </div>
            </v-tab-item>

            <!-- UPDATE -->
            <v-tab-item>
              <v-switch v-model="updateShowDirect" dense inset hide-details class="mt-2 mb-1 ml-2"
                label="Papar nilai lama & baru terus (tanpa hover)"></v-switch>
              <v-simple-table dense class="mt-1">
                <thead><tr>
                  <th>#</th><th>{{ detail.batch.pk_column }}</th>
                  <th>{{ updateShowDirect ? 'Lajur berubah (lama → baru)' : 'Lajur berubah (hover untuk nilai lama)' }}</th>
                  <th>Lihat Semua</th>
                </tr></thead>
                <tbody>
                  <tr v-for="(r,i) in detail.rows.update" :key="'u'+r.id" style="background:#fff8e1">
                    <td>{{ i+1 }}</td>
                    <td>{{ r.pk_value }}</td>
                    <td>
                      <!-- Direct view: old crossed out -> new, no hover -->
                      <template v-if="updateShowDirect">
                        <div v-for="c in (r.changed_cols||[])" :key="c" class="my-1">
                          <b>{{ c }}:</b>
                          <span class="red--text text--darken-1 text-decoration-line-through">{{ (r.old_data||{})[c] }}</span>
                          <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                          <span class="green--text text--darken-2 font-weight-medium">{{ (r.new_data||{})[c] }}</span>
                        </div>
                      </template>
                      <!-- Hover view: chip shows new, tooltip shows old -> new -->
                      <template v-else>
                        <v-tooltip top v-for="c in (r.changed_cols||[])" :key="c">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip x-small class="mr-1 mb-1" color="orange lighten-4" v-bind="attrs" v-on="on">
                              {{ c }}: {{ (r.new_data||{})[c] }}
                            </v-chip>
                          </template>
                          <span>Lama: {{ (r.old_data||{})[c] }} → Baru: {{ (r.new_data||{})[c] }}</span>
                        </v-tooltip>
                      </template>
                    </td>
                    <!-- Unlike Baru/Padam there are two sides to show, so the
                         JSON view carries the full record: what the row holds
                         now, what it would become, and which columns changed. -->
                    <td><v-btn x-small text @click="openRecord('update', r)">Lihat</v-btn></td>
                  </tr>
                  <tr v-if="!detail.rows.update.length"><td colspan="4" class="text-center grey--text">Tiada kemas kini</td></tr>
                </tbody>
              </v-simple-table>
              <div v-if="detail.batch.update_count > detail.rows.update.length" class="caption grey--text mt-1">
                Menunjukkan {{ detail.rows.update.length }} daripada {{ detail.batch.update_count }} (pratonton).
              </div>
            </v-tab-item>

            <!-- DELETE -->
            <v-tab-item>
              <v-simple-table dense class="mt-2">
                <thead><tr><th>#</th><th>{{ detail.batch.pk_column }}</th><th v-for="c in keyCols" :key="c">{{ c }}</th><th>Lihat Semua</th></tr></thead>
                <tbody>
                  <tr v-for="(r,i) in detail.rows.delete" :key="'d'+r.id" style="background:#ffebee">
                    <td>{{ i+1 }}</td>
                    <td>{{ r.pk_value }}</td>
                    <td v-for="c in keyCols" :key="c">{{ (r.old_data||{})[c] }}</td>
                    <td><v-btn x-small text @click="openRecord('delete', r)">Lihat</v-btn></td>
                  </tr>
                  <tr v-if="!detail.rows.delete.length"><td :colspan="keyCols.length+3" class="text-center grey--text">Tiada rekod padam</td></tr>
                </tbody>
              </v-simple-table>
              <div v-if="detail.batch.delete_count > detail.rows.delete.length" class="caption grey--text mt-1">
                Menunjukkan {{ detail.rows.delete.length }} daripada {{ detail.batch.delete_count }} (pratonton).
              </div>
            </v-tab-item>

            <!-- SEMAKAN JULAT (SJ) — advisory range check on new/updated data only -->
            <v-tab-item>
              <div v-if="sjLoading" class="d-flex align-center justify-center py-8">
                <v-progress-circular indeterminate size="28" width="3" color="primary" class="mr-3"></v-progress-circular>
                <span class="grey--text">Menjalankan Semakan Julat ke atas data baru &amp; kemas kini…</span>
              </div>

              <v-alert v-else-if="sjError" type="error" dense text class="mt-3">
                {{ sjError }}
                <v-btn x-small text color="error" class="ml-2" @click="loadSemakanJulat(detail.batch.id)">Cuba semula</v-btn>
              </v-alert>

              <v-alert v-else-if="sjReport && !sjReport.applicable" type="info" dense text class="mt-3">
                Jadual <b>{{ detail.batch.jadual }}</b> tiada peraturan Semakan Julat (SJ).
                Semakan ini terpakai untuk jr2, jr5, hk1&ndash;hk8 dan jre (Jenis Rekod 2, 4, A&ndash;H).
              </v-alert>

              <template v-else-if="sjReport">
                <!-- CLEAN -->
                <v-alert v-if="sjReport.clean" type="success" prominent text border="left" class="mt-3">
                  <div class="text-subtitle-1 font-weight-bold">Tiada Kesalahan Semakan Julat</div>
                  <div class="caption">
                    {{ sjReport.summary.rowsChecked.toLocaleString() }} baris (baru &amp; kemas kini) disemak
                    terhadap {{ sjReport.checked.columns.length }} medan SJ.
                  </div>
                </v-alert>

                <!-- ISSUES -->
                <template v-else>
                  <v-alert type="warning" dense text border="left" class="mt-3">
                    <b>{{ sjReport.summary.rowsWithIssues.toLocaleString() }}</b> baris mempunyai nilai di luar julat
                    ({{ sjReport.summary.totalIssues.toLocaleString() }} kesalahan) daripada
                    {{ sjReport.summary.rowsChecked.toLocaleString() }} baris disemak.<br>
                    Semakan ini adalah <b>amaran</b> — ia tidak menghalang Semak / Sah.
                  </v-alert>
                  <v-simple-table dense class="mt-1">
                    <thead><tr>
                      <th>#</th><th>HID</th><th v-if="sjHasNoair">NOAIR</th><th>Jenis</th><th>Kesalahan (lajur = nilai · julat sah)</th>
                    </tr></thead>
                    <tbody>
                      <tr v-for="(r,i) in sjReport.rows" :key="'sj'+r.id" style="background:#fff8e1">
                        <td>{{ i+1 }}</td>
                        <td>{{ r.hid }}</td>
                        <td v-if="sjHasNoair">{{ r.noair || '-' }}</td>
                        <td>
                          <v-chip x-small :color="r.change_type==='new' ? 'green lighten-4' : 'orange lighten-4'">
                            {{ r.change_type==='new' ? 'Baru' : 'Kemas Kini' }}
                          </v-chip>
                        </td>
                        <td>
                          <div v-for="(iss,k) in r.issues" :key="k" class="my-1">
                            <b>{{ iss.column }}</b> =
                            <span class="red--text text--darken-1 font-weight-medium">{{ iss.value }}</span>
                            <span class="grey--text"> — di luar julat {{ iss.julat }}</span>
                            <v-chip x-small label outlined class="ml-1">{{ iss.code }}</v-chip>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div v-if="sjReport.truncated" class="caption grey--text mt-1">
                    Menunjukkan {{ sjReport.rows.length }} daripada {{ sjReport.summary.rowsWithIssues }} baris bermasalah.
                  </div>
                </template>
              </template>
            </v-tab-item>

            <!-- HISTORY -->
            <v-tab-item>
              <v-timeline dense class="mt-2">
                <v-timeline-item v-for="h in detail.history" :key="h.id" small :color="statusColor(h.stage_to)">
                  <div class="font-weight-bold">{{ h.action }}
                    <span class="caption grey--text">— {{ h.nama || 'Sistem' }} · {{ fmtDate(h.created_at) }}</span>
                  </div>
                  <div class="caption" v-if="h.notes">{{ h.notes }}</div>
                </v-timeline-item>
              </v-timeline>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ============ ACTION (notes) DIALOG ============ -->
    <v-dialog v-model="actionDialog" max-width="500">
      <v-card>
        <v-card-title>{{ actionTitle }}</v-card-title>
        <v-card-text>
          <v-alert v-if="action.stage==='sah' && action.decision==='Sah'" type="warning" dense text>
            Langkah ini akan mengemas kini jadual sebenar ({{ detail && detail.batch.table_name }}). 
            <br><b>Tindakan ini tidak boleh dibatalkan.</b>
          </v-alert>

          <!-- Re-check against the live DB (approve actions only) -->
          <template v-if="isApproveAction">
            <div v-if="recheckLoading" class="my-2">
              <div class="d-flex align-center">
                <v-progress-circular :indeterminate="!recheckProg.pct" :value="recheckProg.pct"
                  size="18" width="2" color="#037769" class="mr-2"></v-progress-circular>
                <span class="caption grey--text">
                  Menyemak semula dengan data semasa…
                  <b v-if="recheckProg.pct">{{ recheckProg.pct }}%</b>
                </span>
              </div>
              <v-progress-linear :indeterminate="!recheckProg.pct" :value="recheckProg.pct"
                color="#037769" rounded height="6" class="mt-2"></v-progress-linear>
              <div class="caption grey--text mt-1" style="min-height:16px">
                <span v-if="recheckProg.label">{{ recheckProg.label }}</span>
                <span v-if="recheckProg.total">
                  · {{ recheckProg.done.toLocaleString() }} / {{ recheckProg.total.toLocaleString() }} {{ recheckProg.unit }}
                </span>
                <span v-if="recheckProg.eta !== null"> · anggaran {{ fmtEta(recheckProg.eta) }} lagi</span>
              </div>
            </div>
            <template v-else-if="recheckReport">
              <v-alert v-if="recheckReport.hasIssues" type="warning" dense text class="mb-2">
                <div class="font-weight-bold mb-1">Sebahagian perubahan tidak lagi terpakai pada data semasa:</div>
                <ul class="pl-4 mb-2" style="font-size:var(--fs-xs)">
                  <li v-if="recheckReport.issues.update_noop.length">
                    {{ recheckReport.issues.update_noop.length }} kemas kini — data semasa sudah sama (tiada perubahan).
                  </li>
                  <li v-if="recheckReport.issues.update_missing.length">
                    {{ recheckReport.issues.update_missing.length }} kemas kini — baris sudah tiada dalam pangkalan data.
                    <b class="error--text">Sah akan ditolak selagi ini tidak dibetulkan.</b>
                  </li>
                  <li v-if="recheckReport.issues.update_conflict.length">
                    {{ recheckReport.issues.update_conflict.length }} kemas kini — nilai semasa telah diubah oleh proses lain.
                  </li>
                  <li v-if="recheckReport.issues.delete_missing.length">
                    {{ recheckReport.issues.delete_missing.length }} padam — baris sudah tiada.
                  </li>
                </ul>
                <div class="caption">
                  Boleh diterap sekarang:
                  <b>{{ recheckReport.applicable.new }}</b> baru,
                  <b>{{ recheckReport.applicable.update }}</b> kemas kini,
                  <b>{{ recheckReport.applicable.delete }}</b> padam
                  <span class="grey--text">
                    (asal {{ recheckReport.recorded.new }}/{{ recheckReport.recorded.update }}/{{ recheckReport.recorded.delete }})
                  </span>
                </div>
                <div class="mt-2 font-weight-medium">
                  Nak teruskan {{ action.stage==='sah' ? 'Sah' : 'Semak' }}?
                </div>
                <!-- Sah runs its own pre-flight and refuses outright if any
                     update no longer has a row to write to, so promising the
                     user they can just carry on would be misleading. -->
                <div v-if="action.stage==='sah' && recheckReport.issues.update_missing.length" class="caption mt-1">
                  Sah akan menolak batch ini tanpa menulis apa-apa. Muat turun CSV batch, betulkan kunci utama
                  (simpan lajur sebagai teks dalam Excel), kemudian muat naik semula.
                </div>
              </v-alert>
              <v-alert v-else type="success" dense text class="mb-2">
                Tiada perubahan pada data semasa.
              </v-alert>
            </template>
          </template>

          <!-- Cancelling at Semak or Sah ends the batch for good, so the reason
               is mandatory: it is the only record of WHY, and it is what the
               uploader reads in Sejarah before re-doing the work.
               Discarding your own draft hides the field entirely — nobody else
               has ever seen that batch, so there is no one to explain it to. -->
          <v-textarea v-if="notesVisible" outlined dense rows="3" v-model="action.notes"
            :label="notesRequired ? 'Nota / sebab pembatalan (wajib)' : 'Nota (pilihan)'"
            :placeholder="notesRequired ? 'Nyatakan sebab batch ini dibatalkan…' : ''"
            :error="notesRequired && notesTouched && !notesFilled"
            :error-messages="notesRequired && notesTouched && !notesFilled
              ? 'Sila nyatakan sebab pembatalan.' : []"
            :disabled="actionProg.active"
            @blur="notesTouched = true"></v-textarea>
          <div v-if="notesVisible && notesRequired && !notesFilled" class="caption grey--text mt-n3 mb-2">
            Sebab ini direkodkan dalam Sejarah dan dilihat oleh pemuat naik.
          </div>

          <!-- Sah applies one statement per recorded row, so a large batch can
               run for minutes. Same single-bar treatment as the upload. -->
          <div v-if="actionProg.active" class="mt-2">
            <div class="d-flex align-center">
              <v-progress-circular :indeterminate="!actionProg.pct" :value="actionProg.pct"
                size="18" width="2" color="#037769" class="mr-2"></v-progress-circular>
              <span class="caption grey--text">
                Melaksanakan perubahan… <b v-if="actionProg.pct">{{ actionProg.pct }}%</b>
              </span>
            </div>
            <v-progress-linear :indeterminate="!actionProg.pct" :value="actionProg.pct"
              color="#037769" rounded height="6" class="mt-2"></v-progress-linear>
            <div class="caption grey--text mt-1" style="min-height:16px">
              <span v-if="actionProg.label">{{ actionProg.label }}</span>
              <span v-if="actionProg.total">
                · {{ actionProg.done.toLocaleString() }} / {{ actionProg.total.toLocaleString() }} {{ actionProg.unit }}
              </span>
              <span v-if="actionProg.eta !== null"> · anggaran {{ fmtEta(actionProg.eta) }} lagi</span>
            </div>
            <v-alert type="warning" dense text class="mt-2 mb-0">
              Jangan tutup halaman ini. Perubahan sedang diterap dalam satu transaksi.
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="actionProg.active" @click="actionDialog = false">Tidak</v-btn>
          <v-btn :color="action.decision==='Batal' ? 'error' : 'success'"
            :dark="!submitBlocked"
            :loading="actionLoading || recheckLoading"
            :disabled="submitBlocked"
            @click="submitAction">Teruskan</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ SAH FAILURE DIALOG ============ -->
    <!-- A failed Sah rolls everything back and leaves the batch at Sah. That
         used to surface only as a snackbar, which is how a batch could look
         finished while nothing had been written. -->
    <v-dialog v-model="sahErrorDialog" max-width="640" scrollable>
      <v-card v-if="sahError">
        <v-card-title class="red lighten-5">
          <v-icon left color="error">mdi-alert-circle-outline</v-icon>
          Perubahan tidak diterap
        </v-card-title>
        <v-card-text class="pt-4" style="max-height:60vh">
          <v-alert type="error" dense text class="mb-3">{{ sahError.message }}</v-alert>

          <v-alert type="info" dense text class="mb-3">
            Batch kekal di peringkat <b>Sah</b> dan tiada perubahan disimpan pada jadual sebenar.
            Sebab kegagalan turut direkodkan dalam tab <b>Sejarah</b>.
          </v-alert>

          <template v-if="sahError.problems && sahError.problems.length">
            <div class="caption font-weight-bold mb-1">
              Baris bermasalah
              <span class="grey--text" v-if="sahError.problemCount > sahError.problems.length">
                (menunjukkan {{ sahError.problems.length }} daripada {{ sahError.problemCount.toLocaleString() }})
              </span>
            </div>
            <v-simple-table dense>
              <thead><tr><th>{{ detail && detail.batch.pk_column }}</th><th>Lajur</th><th>Sebab</th></tr></thead>
              <tbody>
                <tr v-for="(p, i) in sahError.problems" :key="i">
                  <td>{{ p.pk_value }}</td>
                  <td>{{ p.column }}<span v-if="p.value" class="red--text"> = {{ p.value }}</span></td>
                  <td>{{ problemReason(p.reason) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn text class="text-none" :loading="batchCsvLoading" @click="downloadBatchCsv()">
            <v-icon left small>mdi-download</v-icon>Muat Turun CSV Terbitan
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeSahError">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ JSON VIEW DIALOG ============ -->
    <!-- One dialog for all three change types, with a Jadual / JSON toggle.
         Table mode is for reading; JSON mode is for copying, which is why it is
         a textarea (Ctrl+A then Ctrl+C) rather than a <pre>. -->
    <v-dialog v-model="jsonDialog" :max-width="record && record.pair ? 1100 : 760" scrollable>
      <v-card v-if="record">
        <v-card-title>
          {{ record.title }}
          <span v-if="record.pkValue" class="caption grey--text ml-2">
            {{ record.pkLabel }}: {{ record.pkValue }}
          </span>
          <v-spacer></v-spacer>
          <v-btn-toggle v-model="recordMode" mandatory dense class="mr-2">
            <v-btn small value="jadual" class="text-none">
              <v-icon left small>mdi-table</v-icon>Jadual
            </v-btn>
            <v-btn small value="json" class="text-none">
              <v-icon left small>mdi-code-json</v-icon>JSON
            </v-btn>
          </v-btn-toggle>
          <v-btn icon @click="jsonDialog=false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <!-- TABLE MODE -->
        <v-card-text v-if="recordMode === 'jadual'" style="max-height:70vh">
          <!-- Update rows get both sides against ONE key list (the union of old
               and new, changed columns first), so line N is the same column on
               both sides and they scroll as a single block. -->
          <div v-if="record.pair" class="d-flex mb-1">
            <div class="json-col-head">Data Lama</div>
            <div class="json-col-head">Data Baru</div>
          </div>
          <div class="json-pair">
            <table class="json-table">
              <tbody>
                <tr v-for="k in record.keys" :key="k" :class="{ 'json-changed': record.changed.includes(k) }">
                  <td class="json-key">{{ k }}</td>
                  <td class="json-val">{{ record.old[k] }}</td>
                  <td v-if="record.pair" class="json-val json-new">{{ record.new[k] }}</td>
                </tr>
                <tr v-if="!record.keys.length">
                  <td class="text-center grey--text py-4" :colspan="record.pair ? 3 : 2">Tiada data.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="caption grey--text mt-2" v-if="record.pair">
            <span class="json-swatch"></span> lajur yang berubah ({{ record.changed.length }})
          </div>
        </v-card-text>

        <!-- JSON MODE — selectable text, not a <pre>, so it can be copied out. -->
        <v-card-text v-else style="max-height:70vh">
          <!-- :value not {{ }} — interpolated textarea content is not reactive. -->
          <textarea ref="recordJson" class="record-json" readonly spellcheck="false"
            :value="record.json" @focus="$event.target.select()"></textarea>
          <div class="caption grey--text mt-1">
            Klik dalam kotak, kemudian Ctrl+A dan Ctrl+C untuk menyalin.
          </div>
        </v-card-text>

        <v-card-actions v-if="recordMode === 'json'">
          <v-spacer></v-spacer>
          <v-btn small text class="text-none" @click="copyRecordJson">
            <v-icon left small>mdi-content-copy</v-icon>Salin JSON
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ PERMISSION MANAGER (ADMIN) ============ -->
    <v-dialog v-model="permDialog" width="900" scrollable>
      <v-card>
        <v-card-title class="grey lighten-2">
          Urus Kebenaran Pamer Data HIES<v-spacer></v-spacer>
          <v-btn icon @click="permDialog=false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text style="max-height:70vh">
          <v-text-field dense outlined class="mt-3" prepend-inner-icon="mdi-magnify"
            label="Cari nama / emel" v-model="permSearch" clearable hide-details></v-text-field>
          <!-- Paginated: only the current page of rows is rendered, so 1000+
               users no longer freeze the dialog. -->
          <v-data-table
            :headers="permHeaders"
            :items="permUsers"
            :search="permSearch"
            :custom-filter="permFilter"
            :items-per-page="10"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            dense
            class="mt-2 perm-table"
          >
            <template v-slot:item.name="{ item }">
              <div class="text-truncate" style="max-width:170px" :title="item.name">{{ item.name }}</div>
            </template>
            <template v-slot:item.email="{ item }">
              <div class="text-truncate" style="max-width:200px" :title="item.email">{{ item.email }}</div>
            </template>
            <template v-slot:item.roles="{ item }">
              <v-chip x-small v-for="rr in item.roles" :key="rr">{{ rr }}</v-chip>
            </template>
            <template v-slot:item.Upload="{ item }">
              <v-simple-checkbox :value="item.isAdmin || item.permissions.includes('Upload')"
                :disabled="item.isAdmin" @input="togglePerm(item,'Upload')"></v-simple-checkbox>
            </template>
            <template v-slot:item.Semak="{ item }">
              <v-simple-checkbox :value="item.isAdmin || item.permissions.includes('Semak')"
                :disabled="item.isAdmin" @input="togglePerm(item,'Semak')"></v-simple-checkbox>
            </template>
            <template v-slot:item.Sah="{ item }">
              <v-simple-checkbox :value="item.isAdmin || item.permissions.includes('Sah')"
                :disabled="item.isAdmin" @input="togglePerm(item,'Sah')"></v-simple-checkbox>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn small class="btn-teal text-none" :disabled="item.isAdmin" :loading="item._saving" @click="savePerm(item)">Simpan</v-btn>
            </template>
          </v-data-table>
          <div class="caption grey--text mt-2">ADMIN sentiasa mempunyai ketiga-tiga kebenaran secara automatik.</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import i18n from "@/i18n";
import axios from "../../plugins/axios";
import {
  ProgressReader, readEnd, newProgress, applyProgress, fmtEta,
} from "../../services/progressStream";
import { fileStamp } from "../../services/fileStamp";

// Tab order in the batch detail dialog. Named because two places jump straight
// to Sejarah (opening a batch, and a failed Sah) and a bare 4 says nothing.
const TAB_SEJARAH = 4;
import hidServices from "@/services/lookupField";
import getPusingan from "@/services/pusingan";

const negerilookup = new hidServices();
const pusingan = new getPusingan();
const API = process.env.VUE_APP_API_URL;

export default {
  name: "Kawalanpenerbitan",
  data() {
    return {
      snackbar: { show: false, message: "", color: "success", timeout: 4000 },
      myPerms: { permissions: [], isAdmin: false, roleAllowed: true, urlSistemTangkapanData: "" },
      permsLoaded: false,
      // export
      lookupnegeri: [],
      lookupdaerah: [],
      lookuppusingan: [],
      jadualList: [],
      expNegeri: "01", // default: Johor
      expDaerah: null,
      expJadual: null,
      expPusingan: null,
      exporting: false,
      // export preview (snippet of the data that would be exported)
      preview: null,          // { columns, pk, rows, total, shown }
      previewLoading: false,
      previewFilters: {},     // per-column contains-filter, applied server-side
      previewPage: 1,
      previewPerPage: 50,
      previewFilterTimer: null,
      // upload
      uploadFile: null,
      uploading: false,
      uploadFileName: "",
      uploadDialog: false,   // same popup: processing -> result
      uploadError: null,
      uploadMessage: "",
      uploadResult: null,
      // upload progress — "upload" = bytes still on the wire (measurable),
      // "process" = server comparing the file (no progress to report).
      uploadPhase: "upload",
      uploadPct: 0,
      uploadLoaded: 0,
      uploadTotal: 0,
      uploadRate: 0,         // bytes/second
      uploadEta: null,       // seconds left, null when unknown
      uploadStartedAt: 0,
      // server-side progress, fed by the NDJSON stream
      uploadReader: null,    // ProgressReader over the growing response body
      procPct: 0,            // single weighted 0-100 for the whole server step
      procLabel: "",         // what the counter below the bar is counting
      procDone: 0,
      procTotal: 0,
      procUnit: "",
      headTimer: null,
      // batches
      batches: [],
      loadingBatches: false,
      // Server-side counts. Also the badge source before the batch list lands.
      pending: { upload: 0, semak: 0, sah: 0, batal: 0, total: 0 },
      batchTimer: null,
      // Columns carrying an in-header filter are sortable:false so the header
      // click doesn't fight the filter control inside the cell.
      batchHeaders: [
        { text: "#", value: "id" },
        { text: "Jadual", value: "jadual", sortable: false },
        { text: "Negeri", value: "ng", sortable: false },
        { text: "Pusingan", value: "pusingan", sortable: false },
        { text: "Fail", value: "filename" },
        { text: "Baru/Kemas/Padam", value: "counts", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Oleh", value: "uploaded_by_name", sortable: false },
        { text: "Tarikh", value: "created_at", sortable: false },
        { text: "", value: "action", sortable: false },
      ],
      // batch-list filters (all applied client-side over `batches`)
      filters: { jadual: null, ng: null, pusingan: null, status: null, oleh: "", dateFrom: null, dateTo: null },
      olehSearch: "",
      olehTimer: null,
      dateMenu: false,
      dateRange: [],
      // detail
      detailDialog: false,
      detail: null,
      detailTab: 0,
      updateShowDirect: false,
      keyCols: ["HID", "P", "NG", "Bulan", "Tahun"],
      // Semakan Julat (SJ) report for the open batch
      sjReport: null,
      sjLoading: false,
      sjError: null,
      // action
      actionDialog: false,
      action: { stage: "", decision: "", notes: "" },
      notesTouched: false,    // gates the "wajib" error until the user engages
      actionLoading: false,
      recheckReport: null,
      recheckLoading: false,
      batchCsvLoading: false,
      // Sah failure detail — shown as a blocking popup, and the same reason is
      // written to the batch history so it stays visible in Sejarah afterwards.
      sahError: null,         // { message, reason, problems[], problemCount }
      // streamed progress for the two long batch operations
      recheckProg: newProgress(),
      actionProg: newProgress(),
      // json
      jsonDialog: false,
      record: null,          // the row being viewed (one shape for all 3 types)
      recordMode: "jadual",  // 'jadual' | 'json'
      // permissions manager
      permDialog: false,
      permUsers: [],
      permSearch: "",
      permHeaders: [
        { text: "Nama", value: "name" },
        { text: "Emel", value: "email" },
        { text: "Peranan", value: "roles", sortable: false },
        { text: "Upload", value: "Upload", align: "center", sortable: false },
        { text: "Semak", value: "Semak", align: "center", sortable: false },
        { text: "Sah", value: "Sah", align: "center", sortable: false },
        { text: "", value: "action", sortable: false },
      ],
    };
  },
  computed: {
    // ROLE gate — only ADMIN/PP/PO/BPM/BPHPP may use this module. Other roles
    // are shown where to go instead (Sistem Tangkapan Data).
    roleAllowed() {
      return this.myPerms.roleAllowed !== false;
    },
    urlTangkapan() {
      return this.myPerms.urlSistemTangkapanData || "";
    },
    hasAnyAccess() {
      return this.myPerms.isAdmin || (this.myPerms.permissions && this.myPerms.permissions.length > 0);
    },
    // ---- preview column filters ----
    previewFilterActive() {
      return Object.keys(this.previewFilters).some((k) => this.previewFilters[k]);
    },
    // Row number of the first row on this page, so the # column keeps counting
    // across pages instead of restarting at 1.
    previewRowOffset() {
      return (this.previewPage - 1) * this.previewPerPage;
    },
    // ---- batch-list filter options + result ----
    // Filter options are STATIC — the same lookups the Eksport CSV filters use,
    // not derived from the loaded batches (so they don't shift as data changes
    // and stay valid once the list goes server-side/paginated).
    jadualOptions() {
      return (this.jadualList || []).map(j => ({ text: j.label || j.jadual, value: j.jadual }));
    },
    ngOptions() {
      return (this.lookupnegeri || []).map(n => ({ text: `${n.negeriID} · ${n.negeri}`, value: `${n.negeriID}` }));
    },
    pusinganOptions() {
      return (this.lookuppusingan || [])
        .map(p => p.pusingan)
        .filter(p => p && p !== "ALL")
        .filter((v, i, a) => a.indexOf(v) === i)
        .map(p => ({ text: p, value: p }));
    },
    statusOptions() {
      return [
        { text: "Draf", value: "__DRAF__" },
        { text: "Semak", value: "Semak" },
        { text: "Sah", value: "Sah" },
        { text: "Selesai", value: "Selesai" },
        { text: "Batal", value: "Batal" },
      ];
    },
    anyFilterActive() {
      const f = this.filters;
      return !!(f.jadual || f.ng || f.pusingan || f.status || f.oleh || f.dateFrom || f.dateTo);
    },
    dateLabel() {
      if (!this.filters.dateFrom && !this.filters.dateTo) return "Semua";
      return `${this.filters.dateFrom || "…"} → ${this.filters.dateTo || "…"}`;
    },
    filteredBatches() {
      const f = this.filters;
      const oleh = (f.oleh || "").toLowerCase();
      return this.batches.filter(b => {
        if (f.jadual && b.jadual !== f.jadual) return false;
        if (f.ng && b.ng !== f.ng) return false;
        if (f.pusingan && b.pusingan !== f.pusingan) return false;
        if (f.status) {
          const st = b.status || "__DRAF__";
          if (st !== f.status) return false;
        }
        if (oleh && !(b.uploaded_by_name || "").toLowerCase().includes(oleh)) return false;
        if (f.dateFrom || f.dateTo) {
          const d = (b.created_at || "").slice(0, 10); // YYYY-MM-DD
          if (f.dateFrom && d < f.dateFrom) return false;
          if (f.dateTo && d > f.dateTo) return false;
        }
        return true;
      });
    },
    canExport() {
      return !!(this.expNegeri && this.expJadual && this.expJadual.exportable && this.expPusingan);
    },
    // Ketiga-tiga peringkat SENTIASA disenaraikan, ditanda ada/tiada. Dahulu
    // cip dijana terus daripada `myPerms.permissions`, yang hanya mengandungi
    // kebenaran yang DIBERI — jadi pengguna nampak satu cip dan tiada petunjuk
    // bahawa dua peringkat lain wujud tetapi dia tidak layak.
    //
    // Diambil daripada canUpload/canSemak/canSah dan bukan daripada tatasusunan
    // mentah, supaya ADMIN (yang lulus semua tanpa entri dalam senarai itu)
    // ditunjukkan sebagai memiliki ketiga-tiganya.
    // Each stage carries the number of batches waiting on THIS user, so the
    // chips double as a work queue. Counts are derived from the batch list that
    // is already loaded — no extra request, and they refresh with it.
    //   Upload -> own drafts (nobody else can see or finish them)
    //   Semak  -> anything sitting at Semak
    //   Sah    -> anything sitting at Sah
    // `filter` is the Senarai Penerbitan status value the badge jumps to.
    aksesStages() {
      const mine = (b) => this.isMine(b);
      return [
        {
          key: "Upload",
          label: "Upload",
          granted: this.canUpload,
          filter: "__DRAF__",
          count: this.batches.filter((b) => !b.status && mine(b)).length,
        },
        {
          key: "Semak",
          label: "Semak",
          granted: this.canSemak,
          filter: "Semak",
          count: this.batches.filter((b) => b.status === "Semak").length,
        },
        {
          key: "Sah",
          label: "Sah",
          granted: this.canSah,
          filter: "Sah",
          count: this.batches.filter((b) => b.status === "Sah").length,
        },
      ];
    },
    // Cancelled batches this user was part of — uploader or the reviewer who
    // passed it through Semak — that they have not acknowledged. The
    // acknowledgement lives on the server, so it survives a different browser.
    batalBatches() {
      const uid = this.myPerms.uid;
      if (!uid) return [];
      return this.batches.filter((b) =>
        b.status === "Batal" &&
        (this.isMine(b) || String(b.semak_by || "") === String(uid)) &&
        !b.batal_read);
    },
    // Trust the server's count until the list has loaded, so the badge is right
    // on first paint rather than popping in a moment later.
    batalCount() {
      return this.batches.length ? this.batalBatches.length : (this.pending.batal || 0);
    },
    canUpload() {
      return this.myPerms.isAdmin || this.myPerms.permissions.includes("Upload");
    },
    canSemak() {
      return this.myPerms.isAdmin || this.myPerms.permissions.includes("Semak");
    },
    canSah() {
      return this.myPerms.isAdmin || this.myPerms.permissions.includes("Sah");
    },
    isDetailUploader() {
      return !!(this.detail && this.isMine(this.detail.batch));
    },
    sjHasNoair() {
      return !!(this.sjReport && this.sjReport.rows && this.sjReport.rows.some(r => r.noair));
    },
    isApproveAction() {
      return (this.action.stage === "semak" && this.action.decision === "Semak") ||
        (this.action.stage === "sah" && this.action.decision === "Sah");
    },
    // Driven by the payload rather than its own flag, so the dialog and its
    // contents can never disagree.
    sahErrorDialog: {
      get() { return !!this.sahError; },
      set(v) { if (!v) this.sahError = null; },
    },
    // Rows the batch would touch — the size of the CSV the user can download.
    affectedRowCount() {
      if (!this.detail) return 0;
      const b = this.detail.batch;
      return (b.new_count || 0) + (b.update_count || 0) + (b.delete_count || 0);
    },
    // Discarding an unsubmitted draft is private to its uploader, so there is
    // nothing to justify and no audience for the note — the field is hidden
    // rather than shown-and-ignored.
    notesVisible() {
      return this.action.stage !== "bataldraft";
    },
    // Cancelling at Semak or Sah is terminal and needs a recorded reason.
    // Discarding your own draft does not — nobody else ever saw it.
    notesRequired() {
      return this.action.decision === "Batal" &&
        (this.action.stage === "semak" || this.action.stage === "sah");
    },
    notesFilled() {
      return !!String(this.action.notes || "").trim();
    },
    submitBlocked() {
      return this.actionProg.active || (this.notesRequired && !this.notesFilled);
    },
    actionTitle() {
      if (this.action.stage === "submit") return "Hantar Untuk Semakan?";
      if (this.action.stage === "bataldraft") return "Batalkan & Buang Draf?";
      if (this.action.decision === "Batal") return "Batalkan Terbitan?";
      return this.action.stage === "semak" ? "Semak Terbitan?" : "Sahkan & Simpan Perubahan Terbitan";
    },
  },
  mounted() {
    this.init();
    // Warn on browser close/refresh while a file is still being processed.
    window.addEventListener("beforeunload", this.guardUnload);
  },
  beforeDestroy() {
    this.stopBatchAutoReload();
    this.stopHeadCreep();
    window.removeEventListener("beforeunload", this.guardUnload);
  },
  // Block in-app navigation (sidebar/back) while an upload is in flight —
  // leaving mid-request abandons the batch the server is still writing.
  beforeRouteLeave(to, from, next) {
    if (this.uploading) {
      this.notify("Muat naik sedang diproses. Sila tunggu sehingga selesai.", "warning");
      return next(false);
    }
    next();
  },
  methods: {
    notify(message, color = "success") { this.snackbar = { show: true, message, color, timeout: 5000 }; },
    requiresAction(item) {
      if (!item) return false;

      // 1. Draf status: requires action if caller is the uploader and has Upload permission
      if (!item.status || item.status === "Draf" || item.status === "__DRAF__") {
        return this.canUpload && this.isMine(item);
      }

      // 2. Semak status: requires action if caller has Semak permission
      if (item.status === "Semak" && this.canSemak) return true;

      // 3. Sah status: requires action if caller has Sah permission
      if (item.status === "Sah" && this.canSah) return true;

      return false;
    },
    statusColor(s) {
      if (!s) return "blue-grey"; // draft (status NULL)
      return { Semak: "orange", Sah: "blue", Selesai: "green", Batal: "grey" }[s] || "grey";
    },
    stageLabel(s) {
      return s || "Draf"; // draft has no DB status name; show "Draf" in the UI
    },
    fmtDate(d) {
      if (!d) return "";
      try { return new Date(d).toLocaleString("ms-MY"); } catch (e) { return d; }
    },
    async init() {
      await this.loadMyPerms();
      // Defense-in-depth: the router already redirects disallowed roles, but if
      // someone reaches the component directly, bounce them to the no-access page
      // (also avoids calling gated endpoints — a 403 would force a logout).
      if (!this.roleAllowed) {
        this.$router.replace(`/${i18n.locale}/pamerdatahies-tiada-akses`);
        return;
      }
      if (!this.hasAnyAccess) return;
      this.loadPending();     // badge counts, not awaited — nothing waits on it
      await Promise.all([this.loadNegeri(), this.loadPusingan(), this.loadJadual(), this.loadBatches()]);
      // Default negeri is Johor (01) — load its daerah so the dropdown is ready.
      if (this.expNegeri) await this.loadDaerah();
      this.startBatchAutoReload();
    },
    startBatchAutoReload() {
      this.stopBatchAutoReload();
      // Silent refresh every 60s so the list stays current without a spinner flicker.
      this.batchTimer = setInterval(() => { this.loadBatches(true); }, 60000);
    },
    stopBatchAutoReload() {
      if (this.batchTimer) { clearInterval(this.batchTimer); this.batchTimer = null; }
    },
    async loadMyPerms() {
      try {
        const res = await axios.get(`${API}/penerbitan/permissions/me`);
        this.myPerms = res.data.data || { permissions: [], isAdmin: false, roleAllowed: false };
      } catch (e) {
        this.myPerms = { permissions: [], isAdmin: false, roleAllowed: false, urlSistemTangkapanData: "" };
      } finally {
        this.permsLoaded = true;
      }
    },
    async loadNegeri() {
      try {
        const res = await negerilookup.getNegeri();
        this.lookupnegeri = (res.data.data || []).filter(n => n.negeri !== "Admin" && n.negeri !== "Responden")
          .map(n => ({ negeriID: `${n.negeriID}`, negeri: `${n.negeri}` }));
      } catch (e) { /* non-fatal */ }
    },
    async loadPusingan() {
      try {
        const res = await pusingan.getPusinganData();
        this.lookuppusingan = res.data.data || [];
        // Default to the first pusingan (no "Semua" option here).
        if (this.lookuppusingan.length && !this.expPusingan) {
          this.expPusingan = this.lookuppusingan[0];
        }
      } catch (e) { /* non-fatal */ }
    },
    async loadJadual() {
      try {
        const res = await axios.get(`${API}/penerbitan/jadual`);
        this.jadualList = (res.data.data || []).map(j => ({
          ...j,
          label: j.exportable ? `${j.jadual} (${j.table})` : `${j.jadual} — tidak disokong`,
        }));
      } catch (e) { /* non-fatal */ }
    },
    // Negeri changed → reload its Daerah Pentadbiran list, reset DP, re-preview.
    async onNegeriChange() {
      this.expDaerah = null;
      this.lookupdaerah = [];
      await this.loadDaerah();
      this.fetchPreview();
    },
    async loadDaerah() {
      if (!this.expNegeri) { this.lookupdaerah = []; return; }
      try {
        const res = await axios.get(`${API}/lookup/getDP?NG=${this.expNegeri}`);
        this.lookupdaerah = res.data.data || [];
      } catch (e) { this.lookupdaerah = []; }
    },
    // Live snippet of the rows that would be exported for the current filters.
    // Display label for a preview column. Falls back to the real name when the
    // Jadual has no pamer_data config.
    colLabel(c) {
      const map = this.preview && this.preview.columnLabels;
      return (map && map[c]) || c;
    },
    // resetFilters: a new Negeri/Jadual/Pusingan invalidates the column filters
    // (the columns themselves may differ), but paging or typing must not.
    async fetchPreview(resetFilters = true) {
      if (!this.expNegeri || !this.expJadual || !this.expPusingan || !this.expJadual.exportable) {
        this.preview = null;
        return;
      }
      if (resetFilters) {
        this.previewFilters = {};
        this.previewPage = 1;
      }
      this.previewLoading = true;
      try {
        const res = await axios.post(`${API}/penerbitan/preview`, {
          negeri: this.expNegeri,
          dp: this.expDaerah || null,
          jadual: this.expJadual.jadual,
          pusingan: this.expPusingan.pusingan,
          tahun: this.expPusingan.tahun,
          page: this.previewPage,
          perPage: this.previewPerPage,
          colFilters: this.previewFilters,
        });
        this.preview = res.data.data;
        // A filter can shrink the result below the current page; step back so
        // the user is not left staring at an empty page.
        if (this.preview.pageCount < this.previewPage) {
          this.previewPage = this.preview.pageCount;
          return this.fetchPreview(false);
        }
      } catch (e) {
        this.preview = null;
        const msg = (e.response && e.response.data && e.response.data.message) || "Gagal menjana pratonton.";
        this.notify(msg, "error");
      } finally { this.previewLoading = false; }
    },
    // Filtering happens in SQL, so typing is debounced rather than firing a
    // query per keystroke. $set keeps Vue 2 reactive for new keys.
    setPreviewFilter(col, value) {
      if (value) this.$set(this.previewFilters, col, value);
      else this.$delete(this.previewFilters, col);
      clearTimeout(this.previewFilterTimer);
      this.previewFilterTimer = setTimeout(() => {
        this.previewPage = 1;       // a new filter always starts from page 1
        this.fetchPreview(false);
      }, 400);
    },
    clearPreviewFilters() {
      this.previewFilters = {};
      this.previewPage = 1;
      this.fetchPreview(false);
    },
    setPreviewPage(p) {
      this.previewPage = p;
      this.fetchPreview(false);
    },
    setPreviewPerPage(n) {
      this.previewPerPage = Number(n) || 50;
      this.previewPage = 1;
      this.fetchPreview(false);
    },
    async loadPending() {
      try {
        const res = await axios.get(`${API}/penerbitan/pending`);
        this.pending = res.data.data || this.pending;
      } catch (e) { /* badges are decoration — never surface an error for them */ }
    },
    async loadBatches(silent = false) {
      if (!silent) this.loadingBatches = true;
      try {
        const res = await axios.get(`${API}/penerbitan/batches`);
        this.batches = res.data.data || [];
      } catch (e) {
        if (!silent) this.notify("Gagal memuat senarai batch.", "error");
      } finally { if (!silent) this.loadingBatches = false; }
    },
    async downloadCsv() {
      if (!this.expJadual || !this.expJadual.exportable) return;
      this.exporting = true;
      try {
        const res = await axios.post(`${API}/penerbitan/export`, {
          negeri: this.expNegeri,
          dp: this.expDaerah || null,
          jadual: this.expJadual.jadual,
          pusingan: this.expPusingan ? this.expPusingan.pusingan : null,
          tahun: this.expPusingan ? this.expPusingan.tahun : null,
        }, { responseType: "blob" });
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const dpTag = this.expDaerah ? `_DP${this.expDaerah}` : "";
        const p = this.expPusingan ? this.expPusingan.pusingan : "ALL";
        a.download = `${this.expJadual.jadual}_NG${this.expNegeri}${dpTag}_P${p}_${this.fileStamp()}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        this.notify("CSV dimuat turun.");
      } catch (e) {
        // error body arrives as a blob — read it back to show the reason
        let msg = "Gagal mengeksport CSV.";
        try { const t = await e.response.data.text(); msg = JSON.parse(t).message || msg; } catch (_) { /* keep default msg */ }
        this.notify(msg, "error");
      } finally { this.exporting = false; }
    },
    // ---- batch-list filters ----
    onOlehInput() {
      clearTimeout(this.olehTimer);
      this.olehTimer = setTimeout(() => { this.filters.oleh = this.olehSearch || ""; }, 350);
    },
    onOlehClear() {
      clearTimeout(this.olehTimer);
      this.olehSearch = "";
      this.filters.oleh = "";
    },
    onDateRange(val) {
      if (!val || !val.length) { this.filters.dateFrom = null; this.filters.dateTo = null; return; }
      const sorted = [...val].sort();
      this.filters.dateFrom = sorted[0];
      this.filters.dateTo = sorted[1] || sorted[0];
    },
    clearDate() {
      this.dateRange = [];
      this.filters.dateFrom = null;
      this.filters.dateTo = null;
      this.dateMenu = false;
    },
    clearAllFilters() {
      this.filters = { jadual: null, ng: null, pusingan: null, status: null, oleh: "", dateFrom: null, dateTo: null };
      this.olehSearch = "";
      this.dateRange = [];
    },
    // Open the hidden file picker (no visible file input on the page).
    triggerUpload() {
      if (!this.canUpload || this.uploading) return;
      this.$refs.uploadInput && this.$refs.uploadInput.click();
    },
    // A file was chosen — grab it, reset the input (so the same file can be
    // re-picked later), then process immediately.
    onFilePicked(payload) {
      let file = null;
      if (payload instanceof File) {
        file = payload;
      } else if (payload && payload.target && payload.target.files) {
        file = payload.target.files[0];
        payload.target.value = "";
      }
      if (!file) return;
      // Extension check FIRST — only .csv is allowed.
      if (!/\.csv$/i.test(file.name)) {
        this.notify("Hanya fail .csv dibenarkan.", "error");
        this.uploadFile = null;
        return;
      }
      this.uploadFile = file;
      this.uploadFileName = file.name;
      this.doUpload();
    },
    // Native "leave site?" prompt — only while processing.
    guardUnload(e) {
      if (!this.uploading) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    },
    fileStamp,   // YYYYMMDD export suffix — see services/fileStamp
    // Human-readable size, used by the upload progress popup.
    fmtBytes(n) {
      const b = Number(n) || 0;
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
      return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    },
    fmtEta,   // shared with the batch dialogs — see services/progressStream
    // Browser-side transfer progress. Fires while the bytes are still going out
    // over the wire, i.e. BEFORE the server starts comparing anything.
    onUploadProgress(evt) {
      if (!this.uploading) return;
      const total = evt.total || this.uploadTotal || 0;
      const loaded = evt.loaded || 0;
      this.uploadLoaded = loaded;
      if (total) {
        this.uploadTotal = total;
        this.uploadPct = Math.min(100, Math.round((loaded / total) * 100));
      }
      // axios 1.x hands us a smoothed rate; fall back to a plain average so the
      // figure still shows if that field is ever missing.
      const elapsed = (Date.now() - this.uploadStartedAt) / 1000;
      const rate = evt.rate || (elapsed > 0 ? loaded / elapsed : 0);
      this.uploadRate = rate;
      this.uploadEta = rate > 0 && total > loaded ? Math.round((total - loaded) / rate) : null;
      // Last byte handed over -> switch the popup to the comparison phase.
      if (total && loaded >= total) this.uploadPhase = "process";
    },
    // Server-side progress. The upload replies as an NDJSON stream (?stream=1),
    // so the response text grows while the server works: {t:'p',pct} lines
    // carrying one already-weighted 0-100 figure, then a final {t:'end'} line
    // with the real status + body. XHR hands us the partial text on every
    // download-progress tick.
    onServerProgress(evt) {
      const xhr = evt && evt.event && evt.event.target;
      const msg = this.uploadReader && this.uploadReader.read(xhr && xhr.responseText);
      if (!msg) return;
      this.uploadPhase = "process";   // server has the file: step 2 has begun
      this.procLabel = msg.label || "";
      this.procDone = msg.done || 0;
      this.procTotal = msg.total || 0;
      this.procUnit = msg.unit || "";
      if (msg.holdMs) { this.startHeadCreep(msg.holdTo, msg.holdMs); return; }
      this.stopHeadCreep();
      this.setProcPct(msg.pct);
    },
    // The bar only ever moves forward: a step that turns out to be empty jumps
    // ahead rather than rewinding.
    setProcPct(pct) {
      const v = Math.min(100, Math.max(0, Math.round(Number(pct) || 0)));
      if (v > this.procPct) this.procPct = v;
    },
    // Decoding + XLSX.read block the server's event loop, so no progress can
    // arrive during them. The server sends its size-based estimate of how long
    // that will take and we creep the bar across that slice, stopping at its
    // ceiling. The first real reading afterwards overtakes it.
    startHeadCreep(holdTo, holdMs) {
      this.stopHeadCreep();
      const ceiling = Math.max(0, Math.min(100, Number(holdTo) || 0));
      const started = Date.now();
      this.headTimer = setInterval(() => {
        const frac = Math.min(1, (Date.now() - started) / holdMs);
        this.setProcPct(ceiling * frac);
        if (frac >= 1) this.stopHeadCreep();
      }, 200);
    },
    stopHeadCreep() {
      if (this.headTimer) { clearInterval(this.headTimer); this.headTimer = null; }
    },
    async doUpload() {
      if (!this.uploadFile) return;
      this.uploadDialog = true;   // same popup shows processing, then the result
      this.uploading = true;
      this.uploadResult = null;
      this.uploadError = null;
      this.uploadMessage = "";
      this.uploadPhase = "upload";
      this.uploadPct = 0;
      this.uploadLoaded = 0;
      this.uploadTotal = this.uploadFile.size || 0;
      this.uploadRate = 0;
      this.uploadEta = null;
      this.uploadStartedAt = Date.now();
      this.uploadReader = new ProgressReader();
      this.procPct = 0;
      this.procLabel = "";
      this.procDone = 0;
      this.procTotal = 0;
      this.procUnit = "";
      this.stopHeadCreep();
      try {
        const fd = new FormData();
        fd.append("file", this.uploadFile);
        // stream=1 -> NDJSON progress. responseType text because the body is a
        // stream of lines, not one JSON document.
        const res = await axios.post(`${API}/penerbitan/upload?stream=1`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "text",
          onUploadProgress: this.onUploadProgress,
          onDownloadProgress: this.onServerProgress,
        });
        // The stream always returns HTTP 200 (headers go out before the work
        // finishes), so the real outcome lives in the final line.
        const end = readEnd(res.data);
        if (!end) throw new Error("stream-incomplete");
        if (end.status >= 400) {
          this.uploadError = (end.body && end.body.message) || "Gagal memproses fail.";
          return;
        }
        const payload = end.body || {};
        this.uploadResult = payload.data;
        this.uploadMessage = payload.message || "";
        this.uploadFile = null;
        await this.loadBatches();
        this.uploading = false;
        await this.$nextTick();
        // Changes found -> hand straight over to the review popup (it shows the
        // full breakdown). No changes -> keep THIS popup open with the result,
        // otherwise the user would be left with nothing on screen.
        if (payload.data && payload.data.batchId) {
          this.uploadDialog = false;
          this.openBatch(payload.data.batchId);
        }
      } catch (e) {
        if (e && e.message === "stream-incomplete") {
          this.uploadError =
            "Sambungan terputus sebelum pemprosesan selesai. Sila cuba semula.";
          return;
        }
        // Middleware rejections (tiada kebenaran, fail terlalu besar) never reach
        // the streaming handler, so they still arrive as an ordinary JSON error —
        // but responseType 'text' means axios hands it over unparsed.
        let data = e.response && e.response.data;
        if (typeof data === "string") {
          try { data = JSON.parse(data); } catch (err) { data = null; }
        }
        this.uploadError = (data && data.message) || "Gagal memproses fail.";
      } finally { this.uploading = false; this.stopHeadCreep(); }
    },
    closeUploadDialog() {
      this.uploadDialog = false;
      this.uploadError = null;
      this.uploadResult = null;
      this.uploadMessage = "";
    },
    // Download just this batch's affected rows, in the same layout as the main
    // export so the corrected file can be uploaded straight back.
    async downloadBatchCsv(type = null) {
      if (!this.detail) return;
      this.batchCsvLoading = true;
      try {
        const id = this.detail.batch.id;
        const qs = type ? `?type=${type}` : "";
        const res = await axios.get(`${API}/penerbitan/batches/${id}/export-csv${qs}`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        const b = this.detail.batch;
        a.download = `batch${id}_${b.jadual}_NG${b.ng || "X"}_P${b.pusingan || "ALL"}${type ? "_" + type : ""}_${fileStamp()}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        this.notify("CSV batch dimuat turun.");
      } catch (e) {
        let msg = "Gagal memuat turun CSV batch.";
        try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch (_) { /* keep default */ }
        this.notify(msg, "error");
      } finally { this.batchCsvLoading = false; }
    },
    // Was this batch uploaded by the current user? uid is the reliable match;
    // the name comparison is a fallback for older rows with no uploaded_by.
    isMine(b) {
      if (!b) return false;
      const uidMatch = this.myPerms.uid && b.uploaded_by &&
        String(b.uploaded_by) === String(this.myPerms.uid);
      const nameMatch = this.myPerms.name && b.uploaded_by_name &&
        b.uploaded_by_name.trim().toLowerCase() === this.myPerms.name.trim().toLowerCase();
      return !!(uidMatch || nameMatch);
    },
    // Badge click: filter the listing to that stage and bring it into view.
    jumpToBatches(status) {
      this.filters = {
        jadual: null, ng: null, pusingan: null, status,
        oleh: "", dateFrom: null, dateTo: null,
      };
      this.olehSearch = "";
      this.dateRange = [];
      // Own drafts only make sense filtered to the owner.
      if (status === "__DRAF__" && this.myPerms.name) this.filters.oleh = this.myPerms.name;
      // Note: filtering only. The Batal count is cleared per batch when the
      // user actually opens one, not wholesale by looking at the list.
      this.$nextTick(() => {
        const el = this.$refs.batchList;
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    // Acknowledge ONE cancellation, when the user actually opens it. Recorded
    // server-side per user, so it holds across browsers and machines — and the
    // badge counts down one at a time rather than emptying on a glance.
    async markBatalSeen(id) {
      const b = this.batches.find((x) => x.id === id);
      if (!b || b.status !== "Batal" || b.batal_read) return;
      const uid = this.myPerms.uid;
      // Only the people the notice is FOR can clear it.
      if (!uid || !(this.isMine(b) || String(b.semak_by || "") === String(uid))) return;
      // Reflect it at once; the server call makes it stick.
      this.batches = this.batches.map((x) => (x.id === id ? { ...x, batal_read: 1 } : x));
      try {
        await axios.post(`${API}/penerbitan/batal-baca`, { ids: [id] });
        this.loadPending();
      } catch (e) { /* non-fatal: the badge simply returns on the next load */ }
    },
    async resetAndReload() {
      this.clearAllFilters();
      await this.loadBatches();
    },
    async openBatch(id) {
      try {
        const res = await axios.get(`${API}/penerbitan/batches/${id}`);
        this.detail = res.data.data;
        // Open on Sejarah: the first question on opening a batch is almost
        // always "what happened to this", and for a failed Sah the reason is
        // recorded there.
        this.detailTab = TAB_SEJARAH;
        this.detailDialog = true;
        // Opening a cancelled batch is what counts as reading its notice.
        this.markBatalSeen(id);
        // Kick off the SJ range check in the background — the tab shows a
        // spinner until it lands. Not awaited so the popup opens instantly.
        this.loadSemakanJulat(id);
      } catch (e) {
        this.notify("Gagal memuat butiran batch.", "error");
      }
    },
    async loadSemakanJulat(id) {
      this.sjLoading = true;
      this.sjReport = null;
      this.sjError = null;
      try {
        const res = await axios.get(`${API}/penerbitan/batches/${id}/semakan-julat`);
        // Guard against a stale response if the user switched batches meanwhile.
        if (this.detail && this.detail.batch.id === id) this.sjReport = res.data.data;
      } catch (e) {
        this.sjError = (e.response && e.response.data && e.response.data.message) || "Gagal menjalankan Semakan Julat.";
      } finally { this.sjLoading = false; }
    },
    openAction(stage, decision) {
      this.action = { stage, decision, notes: "" };
      this.notesTouched = false;   // don't show the error before they've typed
      this.recheckReport = null;
      this.actionDialog = true;
      // For approve actions (Semak / Sah), re-check the batch against the live
      // DB so we can warn if some changes no longer apply before continuing.
      const isApprove = (stage === "semak" && decision === "Semak") || (stage === "sah" && decision === "Sah");
      if (isApprove && this.detail) this.loadRecheck();
    },
    // Drive a streaming (?stream=1) request and keep `prog` updated as the
    // server reports. Returns the final {status, body} record, or throws
    // "stream-incomplete" if the connection died before the server finished.
    async runStreamed(prog, requestFn) {
      const reader = new ProgressReader();
      const startedAt = Date.now();
      Object.assign(prog, newProgress());
      prog.active = true;
      const res = await requestFn((evt) => {
        const xhr = evt && evt.event && evt.event.target;
        const msg = reader.read(xhr && xhr.responseText);
        if (msg) applyProgress(prog, msg, { startedAt });
      });
      const end = readEnd(res.data);
      if (!end) throw new Error("stream-incomplete");
      return end;
    },
    async loadRecheck() {
      this.recheckLoading = true;
      this.recheckReport = null;
      try {
        const end = await this.runStreamed(this.recheckProg, (onDownloadProgress) =>
          axios.get(`${API}/penerbitan/batches/${this.detail.batch.id}/recheck?stream=1`, {
            responseType: "text",
            onDownloadProgress,
          }));
        this.recheckReport = end.status < 400 ? end.body.data : null;
      } catch (e) {
        this.recheckReport = null; // non-fatal: allow the action to proceed
      } finally {
        this.recheckLoading = false;
        this.recheckProg.active = false;
      }
    },
    async submitAction() {
      if (!this.detail) return;
      this.actionLoading = true;
      const stage = this.action.stage;
      const seg = stage === "bataldraft" ? "batal-draft" : stage; // URL segment
      try {
        const url = `${API}/penerbitan/batches/${this.detail.batch.id}/${seg}`;
        const body = { decision: this.action.decision, notes: this.action.notes };
        // Only Sah applies rows one by one and can take minutes; the rest are a
        // single status update, so they stay plain requests.
        let res;
        if (stage === "sah") {
          const end = await this.runStreamed(this.actionProg, (onDownloadProgress) =>
            axios.post(`${url}?stream=1`, body, { responseType: "text", onDownloadProgress }));
          if (end.status >= 400) {
            const err = new Error("sah-failed");
            err.response = { data: end.body };
            throw err;
          }
          res = { data: end.body };
        } else {
          res = await axios.post(url, body);
        }
        this.notify(res.data.message || "Berjaya.");
        this.actionDialog = false;
        await this.loadBatches();
        if (stage === "bataldraft") {
          this.detailDialog = false; // draft deleted — nothing to reopen
        } else {
          await this.openBatch(this.detail.batch.id); // refresh detail
        }
      } catch (e) {
        const data = (e.response && e.response.data) || {};
        const msg = e && e.message === "stream-incomplete"
          ? "Sambungan terputus semasa melaksanakan perubahan. Sila semak status batch sebelum mencuba semula."
          : data.message || "Tindakan gagal.";
        // A failed Sah is not a passing notification — nothing was written and
        // the user has to act, so it gets a popup with the offending rows. The
        // batch detail is refreshed so the matching Sejarah entry is there too.
        if (stage === "sah" && this.action.decision === "Sah") {
          this.sahError = {
            message: msg,
            reason: data.reason || null,
            problems: data.problems || [],
            problemCount: data.problemCount || (data.problems || []).length,
          };
          this.actionDialog = false;
          await this.loadBatches();
          await this.openBatch(this.detail.batch.id);   // opens on Sejarah
        } else {
          this.notify(msg, "error");
        }
      } finally { this.actionLoading = false; this.actionProg.active = false; }
    },
    // Plain-language reason for each problem code the API reports.
    problemReason(code) {
      switch (code) {
        case "hilang": return "Baris tiada dalam pangkalan data (kunci utama tidak sepadan)";
        case "kosong": return "Tiada nilai yang boleh ditulis untuk lajur berubah";
        case "padam-hilang": return "Baris untuk dipadam sudah tiada";
        case "format": return "Nilai rosak akibat format Excel (notasi saintifik)";
        default: return code || "-";
      }
    },
    closeSahError() { this.sahError = null; },
    /**
     * Open the record popup for any change type.
     *
     * 'new' and 'delete' have a single side (the values that would be written,
     * or the values that would be lost). 'update' has two, rendered against ONE
     * key list — the union of old and new with changed columns first — so the
     * same column sits on the same line on both sides.
     */
    openRecord(type, r) {
      const pkLabel = this.detail ? this.detail.batch.pk_column : "";
      const fmt = (v) => (v === null || v === undefined ? "" : String(v));
      const isUpdate = type === "update";

      if (!isUpdate) {
        const src = (type === "delete" ? r.old_data : r.new_data) || {};
        const keys = Object.keys(src);
        const vals = {};
        for (const k of keys) vals[k] = fmt(src[k]);
        this.record = {
          title: type === "delete" ? "Rekod untuk Dipadam" : "Rekod Baru",
          pkLabel, pkValue: r.pk_value || "",
          pair: false, keys, changed: [], old: vals, new: {},
          json: JSON.stringify(src, null, 2),
        };
      } else {
        const oldData = r.old_data || {};
        const newData = r.new_data || {};
        const changed = r.changed_cols || [];
        const rest = [...new Set([...Object.keys(oldData), ...Object.keys(newData)])]
          .filter((k) => !changed.includes(k))
          .sort();
        const keys = [...changed, ...rest];
        const pick = (src) => {
          const o = {};
          for (const k of keys) o[k] = fmt(src[k]);
          return o;
        };
        this.record = {
          title: "Perubahan Rekod",
          pkLabel, pkValue: r.pk_value,
          pair: true, keys, changed,
          old: pick(oldData), new: pick(newData),
          json: JSON.stringify({
            [pkLabel || "pk"]: r.pk_value,
            lajur_berubah: changed,
            data_lama: oldData,
            data_baru: newData,
          }, null, 2),
        };
      }
      this.recordMode = "jadual";
      this.jsonDialog = true;
    },
    async copyRecordJson() {
      const el = this.$refs.recordJson;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.record.json);
        } else if (el) {
          // Older browsers / non-secure origins have no clipboard API.
          el.select();
          document.execCommand("copy");
        }
        this.notify("JSON disalin.");
      } catch (e) {
        if (el) el.select();
        this.notify("Tidak dapat menyalin automatik — sila tekan Ctrl+C.", "warning");
      }
    },
    // ---- permission manager ----
    // Match only against name/email (v-data-table calls this per row/column).
    permFilter(value, search, item) {
      if (!search) return true;
      const q = search.toLowerCase();
      return (item.name || "").toLowerCase().includes(q) || (item.email || "").toLowerCase().includes(q);
    },
    async openPermManager() {
      this.permDialog = true;
      try {
        const res = await axios.get(`${API}/penerbitan/permissions`);
        this.permUsers = (res.data.data || []).map(u => ({ ...u, _saving: false }));
      } catch (e) {
        this.notify("Gagal memuat senarai pengguna.", "error");
      }
    },
    togglePerm(u, perm) {
      if (u.isAdmin) return;
      const i = u.permissions.indexOf(perm);
      if (i >= 0) u.permissions.splice(i, 1); else u.permissions.push(perm);
    },
    async savePerm(u) {
      u._saving = true;
      try {
        await axios.put(`${API}/penerbitan/permissions/${u.id}`, { permissions: u.permissions });
        this.notify(`Kebenaran ${u.name} dikemas kini.`);
      } catch (e) {
        this.notify("Gagal menyimpan kebenaran.", "error");
      } finally { u._saving = false; }
    },
  },
};
</script>

<style scoped>
/* Sepadan dengan halaman lain (cth. `.bulanan-purata`): padding 16px dan
   margin negatif untuk membatalkan padding lalai <v-main>, supaya jadual
   memakai keseluruhan lebar yang ada. */
.Kawalanpenerbitan {
  padding: 16px;
  margin-left: -12px;
  margin-right: -12px;
}
/* Kepala kad — corak yang sama seperti halaman lain: jalur putih dengan ikon
   dan tajuk, bukan `grey lighten-2` + `text-h5` (24px) yang dipakai dahulu. */
.kad__tajuk {
  border-bottom: 1px solid #dbeeeb;
}
.kad__tajuk .v-icon {
  color: #037769;
}
/* `v-card-title` Vuetify ialah flex tanpa balutan; tanpa ini cip + butang
   menolak tajuk keluar pada skrin sempit. */
.kp-head {
  flex-wrap: wrap;
  gap: 8px;
}
.kp-akses {
  flex-wrap: wrap;
  gap: 4px;
}
.kp-akses__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  margin-right: 4px;
}
/* Cip kebenaran: teal LEMBUT dan bukan pejal. Ia maklumat rujukan, bukan
   tindakan — cip teal pejal bersaing dengan butang teal pejal di sebelahnya. */
.kp-chip.v-chip {
  background: #e6f6f5 !important;
  color: #026254 !important;
  font-weight: 700;
  letter-spacing: .3px;
}
.kp-chip .v-icon { color: #026254 !important; }
/* Tiada kebenaran: kelabu pudar dengan birai putus-putus. Birai itu penting —
   warna sahaja tidak mencukupi untuk pengguna buta warna, dan ikon tolak di
   dalam cip mengulangi maklumat yang sama dalam bentuk ketiga. */
.kp-chip--off.v-chip {
  background: transparent !important;
  color: #9db3b0 !important;
  border: 1px dashed #cfd8dc;
  font-weight: 600;
}
.kp-chip--off .v-icon { color: #b0bec5 !important; }
/* Cip ADMIN membawa lebih berat: ia keistimewaan, bukan satu kebenaran biasa. */
.kp-chip--admin.v-chip {
  background: #01463e !important;
  color: #ffffff !important;
}
.kp-chip--admin .v-icon { color: #7fe9dd !important; }
/* Menggantikan `color="primary"` — primary Vuetify ialah biru. Literal, bukan
   var(--teal-*): pembolehubah itu ditakrif pada `.Menu`, bukan :root. */
.btn-teal.v-btn {
  background: #037769 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-teal.v-btn:hover { background: #026254 !important; }
/* Vuetify memberi `.v-icon` warnanya SENDIRI (rgba(0,0,0,.54)) yang tidak
   mewarisi warna butang — pada latar teal ia menjadi kelabu gelap atas hijau
   gelap dan hampir tidak kelihatan. Diwarnakan putih secara eksplisit. */
.btn-teal.v-btn .v-icon { color: #ffffff !important; }
.btn-teal.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .38) !important; }
.btn-teal.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
/* Dahulu 12px — mengecilkan SEMUA sel jadual pada halaman ini di bawah saiz
   badan sistem. Sekarang jadual mengikut tangga taip yang sama seperti Jadual
   Awalan dan Julat Harga. */
.Kawalanpenerbitan >>> .v-data-table td { font-size: var(--fs-sm); }

/* ---- Export preview table (many columns → horizontal scroll, sticky header) ---- */
.preview-wrap {
  overflow: auto;
  max-height: 440px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
}
.preview-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--fs-xs);
  white-space: nowrap;
  min-width: 100%;
}
.preview-table th,
.preview-table td {
  padding: 5px 10px;
  border-bottom: 1px solid #eee;
  border-right: 1px solid #f2f2f2;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.preview-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #eceff1;
  font-weight: 600;
  color: #37474f;
  text-align: left;
  border-bottom: 2px solid #cfd8dc;
}
/* Two sticky header rows: labels, then the per-column filter boxes under them.
   The label row gets an explicit height so the filter row's offset is exact —
   without it the second row overlaps the first as soon as the table scrolls. */
.preview-table thead tr:first-child th { top: 0; height: 28px; }
.preview-table thead tr.filter-row th {
  top: 28px;
  z-index: 2;
  background: #f5f7f8;
  border-bottom: 2px solid #cfd8dc;
  padding: 3px 6px;
}
.preview-table thead tr.filter-row .idx-col { z-index: 3; background: #f5f7f8; text-align: center; }
.col-filter {
  width: 100%;
  min-width: 70px;
  font-size: var(--fs-xs);
  font-weight: 400;
  padding: 2px 6px;
  border: 1px solid #cfd8dc;
  border-radius: 3px;
  background: #fff;
  outline: none;
}
.col-filter:focus { border-color: #037769; box-shadow: 0 0 0 2px rgba(3, 119, 105, 0.12); }
.col-filter::placeholder { color: #b0bec5; font-weight: 400; }
/* Mirrors the v-data-table footer used by Senarai Penerbitan: same order, same
   spacing, same bottom-right alignment, so both tables read as one design. */
.preview-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 6px 4px 0;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}
.preview-footer .pf-label { white-space: nowrap; }
.preview-footer .pf-range { margin: 0 12px; white-space: nowrap; }
.preview-footer .pf-select {
  max-width: 64px;
  margin: 0;
  padding: 0;
  font-size: 0.75rem;
}
.preview-footer .pf-select >>> .v-input__slot { margin: 0; }
.preview-footer .pf-select >>> .v-input__slot::before { border-style: none; }
.preview-footer .pf-select >>> .v-select__selections { font-size: 0.75rem; }

/* ---- work-notification badges on the permission chips ---- */
.kp-count {
  min-width: 22px;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  padding: 0 6px !important;
  margin-left: -4px;
  background: #e0e0e0 !important;
  color: #757575 !important;
  cursor: pointer;
}
.kp-count--on {
  background: #d32f2f !important;
  color: #fff !important;
}
/* Solid red, white text, no icon — count inline as "Batal 6".
   Qualified with .v-chip to match `.kp-chip.v-chip` above: that base rule is
   also !important, so on a bare `.kp-chip--batal` the extra class made the base
   win on specificity and the chip stayed pale teal. Same form as
   .kp-chip--off / .kp-chip--admin. */
.kp-chip--batal.v-chip {
  background: #d32f2f !important;
  color: #ffffff !important;
  font-weight: 700;
  cursor: pointer;
}
.kp-chip--batal.v-chip:hover { background: #b71c1c !important; }

/* ---- batch detail tabs ---- */
/* The tabs previously looked like a row of static labels. A tinted rail, a
   divider, hover feedback and a count pill per tab make them read as controls. */
.kp-tabs {
  border-bottom: 1px solid #e0e0e0;
  border-radius: 6px 6px 0 0;
  background: #f5f7f8;
}
.kp-tabs >>> .v-tab {
  font-weight: 600;
  font-size: var(--fs-sm);
  letter-spacing: 0;
  min-width: 0;
  padding: 0 14px;
  color: #546e7a !important;
  border-right: 1px solid #e6ebed;
}
.kp-tabs >>> .v-tab:hover {
  background: #eceff1;
  color: #037769 !important;
}
.kp-tabs >>> .v-tab--active {
  background: #fff;
  color: #037769 !important;
}
.kp-tabs >>> .v-tabs-slider-wrapper { height: 3px !important; }
/* Count pill inside a tab — neutral by default, coloured per change type. */
.kp-tab-count {
  display: inline-block;
  min-width: 20px;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 10px;
  background: #cfd8dc;
  color: #37474f;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.kp-tab-count--new { background: #c8e6c9; color: #1b5e20; }
.kp-tab-count--upd { background: #ffe0b2; color: #e65100; }
.kp-tab-count--del { background: #ffcdd2; color: #b71c1c; }
.kp-tab-count--err { background: #d32f2f; color: #fff; }

/* ---- old / new record comparison ---- */
/* One table for both sides rather than two panes: a shared row per column is
   what keeps the values aligned, and it scrolls as a single block. */
.json-col-head {
  font-size: var(--fs-xs);
  font-weight: 600;
  color: #546e7a;
}
/* Copy target for JSON mode: a real textarea so Ctrl+A selects only this. */
.record-json {
  width: 100%;
  min-height: 46vh;
  font-family: monospace;
  font-size: var(--fs-xs);
  line-height: 1.45;
  padding: 10px 12px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  background: #fafafa;
  color: #263238;
  resize: vertical;
  white-space: pre;
  overflow: auto;
}
.record-json:focus { outline: none; border-color: #037769; box-shadow: 0 0 0 2px rgba(3, 119, 105, 0.12); }
.json-col-head:first-child { margin-left: 180px; flex: 1; }
.json-col-head:last-child { flex: 1; }
.json-pair {
  overflow: auto;
  max-height: 55vh;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}
.json-table { width: 100%; border-collapse: collapse; font-size: var(--fs-xs); font-family: monospace; }
.json-table td { padding: 3px 8px; border-bottom: 1px solid #f2f2f2; vertical-align: top; }
.json-table .json-key {
  width: 180px;
  min-width: 180px;
  font-weight: 600;
  color: #37474f;
  background: #fafafa;
  position: sticky;
  left: 0;
}
.json-table .json-val { width: 50%; word-break: break-word; }
.json-table .json-new { border-left: 1px solid #eee; }
.json-table .json-changed { background: #fff8e1; }
.json-table .json-changed .json-key { background: #ffecb3; }
.json-table .json-changed .json-new { color: #2e7d32; font-weight: 600; }
.json-swatch {
  display: inline-block;
  width: 10px; height: 10px;
  background: #fff8e1;
  border: 1px solid #ffca28;
  vertical-align: middle;
  margin-right: 4px;
}
.preview-table tbody tr:nth-child(even) { background: #fafafa; }
.preview-table tbody tr:hover { background: #e3f2fd; }
.preview-table .idx-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: inherit;
  color: #90a4ae;
  text-align: right;
  min-width: 34px;
}
.preview-table thead .idx-col { z-index: 3; background: #eceff1; }
.preview-table .pk-col {
  background: #fff8e1;
  font-weight: 600;
}
.preview-table thead .pk-col { background: #ffecb3; }
.preview-table .empty-cell { color: #bbb; }
.preview-empty { border: 1px dashed #e0e0e0; border-radius: 6px; }

/* ===== Senarai Penerbitan: normal (not dense) table text ===== */
.batch-table >>> tbody td { font-size: var(--fs-sm); height: 44px; }
.batch-table >>> thead th { vertical-align: top; padding-top: 8px; padding-bottom: 8px; }
/* tajuk lajur di atas setiap penapis */
.batch-table >>> thead th .th-label,
.batch-table >>> thead th span:not(.th-label) { font-size: var(--fs-xs); }

/* ---------- Bar penapis eksport ----------
   Jalur teal yang sama seperti Kadar Respon / Jadual Awalan: ia menandakan
   "kawasan penapis" pada setiap halaman dalam sistem. */
.filter-bar {
  background: #f7fcfb;
  border: 1px solid #dbeeeb;
  border-radius: 4px;
  padding: 10px 12px 12px;
}
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}
.filter-bar >>> .v-input fieldset { border-color: #cfeae7; }
.filter-bar >>> .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar >>> .v-select__selection--comma { color: #21403c; }
.filter-bar >>> .v-input__append-inner .v-icon { color: #7aa8a2; }

/* ===== Penapis dalam kepala jadual =====
   Bahasa visual disamakan dengan bar penapis halaman lain — jalur teal pucat,
   label kening huruf besar, birai teal yang menjadi pekat apabila difokus —
   tetapi KEDUDUKANnya kekal dalam kepala lajur: setiap penapis terikat pada
   lajurnya sendiri, jadi memindahkannya keluar mengubah cara jadual ini
   digunakan. */
.batch-table >>> thead th {
  background: #f7fcfb;
  border-bottom: 1px solid #dbeeeb !important;
  font-size: var(--fs-3xs) !important;
  font-weight: 700 !important;
  letter-spacing: 1px !important;
  text-transform: uppercase !important;
  color: #7aa8a2 !important;
  vertical-align: middle !important;
}
.batch-table >>> tbody tr {
  cursor: pointer;
}
.th-filter { display: flex; flex-direction: column; gap: 4px; height: 100%; }
.th-filter--center { justify-content: center; min-height: 52px; }
.th-label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
}
.th-input { min-width: 96px; max-width: 150px; }
/* Kekal `solo flat` dan bukan `outlined`: kepala jadual perlu kawalan setinggi
   30px, manakala medan `outlined dense` ialah 40px — enam daripadanya akan
   menjadikan baris kepala setinggi dua baris data. Birai dilukis sendiri di
   sini supaya rupanya sama dengan medan outlined pada halaman lain. */
.th-input >>> .v-input__slot {
  min-height: 30px !important;
  padding: 0 8px !important;
  background: #ffffff !important;
  border: 1px solid #cfeae7 !important;
  border-radius: 4px !important;
  box-shadow: none !important;
}
.th-input.v-input--is-focused >>> .v-input__slot {
  border-color: #037769 !important;
}
.th-input >>> .v-select__selections,
.th-input >>> input { font-size: var(--fs-xs); color: #21403c; }
.th-input >>> .v-select__selections { min-height: 28px; }
.th-input >>> .v-input__append-inner { margin-top: 3px !important; }
.th-input >>> .v-text-field__prepend-inner { margin-top: 4px !important; }
.th-input >>> .v-icon { font-size: 16px; color: #7aa8a2; }
.th-date {
  min-width: 96px;
  height: 30px !important;
  font-size: var(--fs-xs) !important;
  border-color: #cfeae7 !important;
  color: #21403c !important;
  background: #ffffff;
  letter-spacing: normal;
}
.th-date:hover { border-color: #037769 !important; }
.th-date >>> .v-icon { color: #7aa8a2; }

/* ===== Batch detail popup — normal, readable text ===== */
.kp-detail { font-size: var(--fs-sm); }
.kp-detail >>> .caption { font-size: var(--fs-xs) !important; }
.kp-detail >>> table th,
.kp-detail >>> table td { font-size: var(--fs-sm) !important; height: 40px; }
.kp-detail >>> .v-card__title { font-size: var(--fs-lg); }
.kp-detail >>> .v-tab { font-size: var(--fs-sm); }

/* Butang muat naik — sama seperti .btn-teal tetapi hijau. */
.btn-green.v-btn {
  background: #0EA5A4 !important;
  color: #ffffff !important;
  letter-spacing: normal;
}
.btn-green.v-btn:hover { background: #0b8483 !important; }
.btn-green.v-btn .v-icon { color: #ffffff !important; }
.btn-green.v-btn--disabled {
  background: #cfd8dc !important;
  color: rgba(0, 0, 0, .38) !important;
}
.btn-green.v-btn--disabled .v-icon { color: rgba(0, 0, 0, .38) !important; }

/* ===== Radiating green animation with thick green border for buttons requiring action ===== */
.pulse-action-btn {
  position: relative !important;
  border: 2px solid #037769 !important;
  animation: greenRadiate 1.8s ease-in-out infinite !important;
  font-weight: 700 !important;
}

@keyframes greenRadiate {
  0% {
    box-shadow: 0 0 0 0 rgba(3, 119, 105, 0.75), 0 0 8px rgba(3, 119, 105, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(3, 119, 105, 0), 0 0 0 0 rgba(3, 119, 105, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(3, 119, 105, 0), 0 0 0 0 rgba(3, 119, 105, 0);
  }
}
</style>
